import { Component, HostBinding, Inject, OnInit, ViewChild, DOCUMENT } from '@angular/core';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { Subject, Observable, firstValueFrom, scheduled, asapScheduler, throwError } from 'rxjs';
import { catchError, last, map, tap } from 'rxjs/operators';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { TranslateService } from '@ngx-translate/core';
import { resize as resizeImageToUrl } from 'resize-image';

import { AppService, getCartItemFromCartItemProduct } from './app.service';
import { API_RESPONSE_CODE_DOCUMENT_NOT_FOUND, ApiService } from './shared/api.service';
import {
    IEventPageResponse, IGetEventTokenResponse, IValidationResult, IDependency, IMenuItem, IField, IStoredIdentityToken,
    IGetProcessFormResponse, IStoredEventToken, IErrorNotification, IJavascriptIntegrationScript, IEventSettings,
    IObjectWithId, ApiError, IFieldWithInputElement, IPaymentProductResponse, IProductIdWithQuantity,
    IPayResponse, IOrder, IPaymentProduct, IAsset, IImageFieldOptions, IProfileEvent,
    PaymentProvider, ICyberSourcePaySettings, IStripePaySettings, IProfilePageResponse,
    ILinkTokenPayload, ITokenInfo, IInvoicePaySettings, IRegisterToActivitiesRequest,
    IGetActivitiesCartResponse, LoginMethod, IMonerisPaySettings, IMonerisPaymentArgs,
    MonerisPaymentResponseCode, IPaymentIntegrationWebhookRequest, IIngenicoPaySettings, ICompleteProcessFormResponse,
    SortDirection, IEventTokenPayload, IReverePaySettings, IReverePaymentResponse,
    IMolliePaySettings,
    ICartItemProduct,
    IPayOnePaySettings,
    ISaferpaySettings
} from './shared/interfaces';
import {
    IntegrationType, GoogleTagManagerEvent, LoginStep, SearchParamName, SpecialPageId, TealiumEvent,
    TranslationKey, SideMenuItemId, SearchParamValue, DateConversion, TRANSLATION_MAP,
    RevereAttributeName, RevereDataVariantAttributeValue
} from './enums';
import { environment } from '../environments/environment';
import { AppDomService } from './app-dom.service';
import { LoginPageDomService } from './login-page-dom.service';
import { ValidatorService } from './shared/validator.service';
import { StorageService } from './shared/storage.service';
import { NotificationsService } from './shared/notifications.service';
import { EventPublish, EventType, FieldMode, NotificationMessageType } from './shared/enums';
import { googleAnalyticsSnippet, googleTagManagerSnippet, tealiumSnippet } from './shared/javascript-integration-snippets';
import { IProductVisibility, IProductWithQuantity, IShoppingCartChangedData, PayById } from './shared/interfaces';
import {
    DocumentFieldCallbackDataClickedItemName, IApplyDependenciesResult, IDocumentFieldCallbackData, IIdWithValue, IImageCropperDialogConfig,
    IImageCropperImageFileSelectedEventArgs, IImageFieldCallbackData, IModalDialogConfig, IProfileFlags,
    ISideMenuItem, IUploadDocumentResult, ActivitiesCartDisplayType, AttributeName, IEnterEmailDialogConfig, ElementId, IMyEventsData,
    IRevereCheckoutDialogConfig, IRevereCheckoutDialogResult, IRevereCheckoutForm, IMergeParticipantWithUtmObjectResult,
    QueryParamsInput,
} from './interfaces';
import { ModalDialogService } from './modal-dialog.service';
import { CropperModalDialogService } from './cropper-modal-dialog.service';
import { EmailCodeModalDialogService } from './email-code-modal-dialog.service';
import { UtilsService } from './shared/utils.service';
import { ApiUtilsService } from './shared/api-utils.service';
import { MomentService } from './shared/moment.service';
import { IAccountResetArgs } from './profile/login/interfaces';
import { ICartActivityItem } from './activities/cart/list/interfaces';
import { SubjectsService } from './shared/subjects.service';
import { EnterEmailModalDialogService } from './enter-email-modal-dialog.service';
import { MonerisService } from './moneris.service';
import { IShoppingCartV2ChangedData, IShoppingCartV2Data } from './shopping-cart-v2/interfaces';
import { ShoppingCartV2Component } from './shopping-cart-v2/shopping-cart-v2.component';
import { IRowActionObject, ITableData, ITableSettings } from './table/interfaces';
import { MyEventsService } from './my-events.service';
import { SharedService } from './shared/shared.service';
import { RevereChechkoutModalDialogService } from './revere-checkout-modal-dialog.service';
import { hasDependencyActivities, hasDependencyProducts } from '@azavista/advanced-search/lib/dependency/evaluate';
import { Workflow } from '@azavista/advanced-search';
import { ActivitiesCartListService } from './activities/cart/list/activities-cart-list.service';
import { ShoppingCartV2Service } from './shopping-cart-v2/shopping-cart-v2.service';
import { AppLoggerService, LogLevel } from './shared/app-logger.service';
import { ProfileSubscriptionsService } from './profile-subscriptions.service';
import { EventSettings } from '@azavista/advanced-search/lib/search-types';
import { AddToEventModalDialogService } from './add-to-event-modal-dialog.service';

import { DonationProductItem } from '@azavista/payments';
import { LANGUAGE_DEPENDENCY_RECORD, LanguageSelectorService, SupportedLanguageIsoId } from './shared/language-selector.service';
import { MultiRegistrationService } from './shared/multi-registration/multi-registration.service';
import { EventDataService } from './shared/event-data.service';
import { getAllSubParticipants } from './shared/multi-registration/utils';
import { MultiRegistrationFormService } from './shared/multi-registration/multi-registration-form.service';

// TODO: Use @types/stripe-v2
declare const Stripe: any;
declare const monerisCheckout: any;
declare const CollectJS: any;

const INITIAL_ACTIVITY_CART_ITEMS: ICartActivityItem[] = [];

type PageIdTarget = string;
type PageIdRedirectMap = {
    [pageId: string]: PageIdTarget;
};
type ResizeTarget = {
    width: number;
    height: number;
};

@Component({
    selector: 'aza-rws-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
    eventId = '';
    tealiumRegistration = false;

    loadingButtonClassName = 'loading-button';
    socialWallDisplay = 'none';
    readonly defaultLanguage = 'en-US';

    loginPageResponse: IEventPageResponse;
    // TODO: This must be returned by a function which will go to API service
    //       so when some response returns token, it will be set in the API service
    //       and this component will get always the latest token
    eventTokenResponse: IGetEventTokenResponse;
    private _eventSettings: IEventSettings;
    public get eventSettings(): IEventSettings {
        return this._eventSettings;
    }
    public set eventSettings(value: IEventSettings) {
        this._eventSettings = value;
        this.eventDataSvc.eventSettings = value;
    }
    processFormResponse: IGetProcessFormResponse;
    loginStep = LoginStep.sendEmail;

    public get currentLanguage(): string {
        return this.languageSelectorSvc.currentLanguage;
    }
    public set currentLanguage(value: string) {
        this.languageSelectorSvc.currentLanguage = value;
        const selectedLanguageWithoutLocality = value.split(/\-|_/gm)[0]; // split by '-' or '_'
        this.doc.getElementsByTagName('html')[0].lang = selectedLanguageWithoutLocality;
        this.contentLanguageElement.content = selectedLanguageWithoutLocality;
        this.domSvc.setSelectElementValueOptionsTranslations(this.eventParticipantFields, this.currentLanguage);
    }
    storedIdentityToken: IStoredIdentityToken;
    // storedEventTokens: IStoredEventToken[];
    private _eventDependencies: IDependency[];
    public get eventDependencies(): IDependency[] {
        return this._eventDependencies;
    }
    public set eventDependencies(value: IDependency[]) {
        this._eventDependencies = value;
        this.eventDataSvc.eventDependencies = value;
    }
    private _eventParticipantFields: IField[];
    public get eventParticipantFields(): IField[] {
        return this._eventParticipantFields;
    }
    public set eventParticipantFields(value: IField[]) {
        this._eventParticipantFields = value;
        this.eventDataSvc.eventParticipantFields = value;
    }
    private _eventFields: IField[];
    public get eventFields(): IField[] {
        return this._eventFields;
    }
    public set eventFields(value: IField[]) {
        this._eventFields = value;
        this.eventDataSvc.eventFields = value;
    }
    private _eventParticipant: IObjectWithId;
    public get eventParticipant(): IObjectWithId {
        return this._eventParticipant;
    }
    public set eventParticipant(value: IObjectWithId) {
        this._eventParticipant = value;
        this.eventDataSvc.eventParticipant = value;
    }
    updatedEventParticipant: Partial<IObjectWithId> = {};
    eventMenuItems: IMenuItem[];
    pageData: IEventPageResponse;
    dynamicEl: HTMLDivElement;
    productResponse: IPaymentProductResponse;
    orders: IOrder[] = [];
    order: IOrder;

    email = '';
    username = '';
    registrationCode = '';
    shouldShowYourFormHasBeenSubmittedMessage = false;
    javascriptIntegrations: IJavascriptIntegrationScript[];

    errorFormFields: Record<string, boolean> = {};
    hiddenFieldIds: string[] = [];
    lastShoppingCartChangedData?: IShoppingCartChangedData;
    documentFieldsFilesMap: Record<string, IDocumentFieldCallbackData> = {};
    deletedDocumentFieldsFilesMap: Record<string, boolean> = {};

    showCropper = true;
    showCropButton = false;
    imageCroppingDisabled = false;
    imgFieldSrc: any;
    cropperAspectRatio = 1;
    cropperMaxWidth = 0;
    cropperMaxHeight = 0;
    cropperImageChangeEvent: any;
    croppedImageBytes: Uint8Array<ArrayBuffer>;
    croppedImageBase64: string;
    croppingFieldId: string;
    croppedImagesMap: Record<string, { buffer: Uint8Array<ArrayBuffer>, file: File | undefined}> = {};
    deletedImageFieldValuesMap: Record<string, boolean> = {};

    profileFlags: IProfileFlags = {
        showProfileButton: false,
        showLoginButton: false,
        showLogin: false,
        showSideMenu: false,
        showProfile: false,
        showEvents: false,
        showChangePassword: false,
        showSubscriptions: false,
        showOrders: false,
        showContacts: false
    };
    sideMenuItems: ISideMenuItem[];

    featureFlags = {
        // profileEnabled: false
    };

    profilePageResponse: IProfilePageResponse;
    applyDependenciesResult: IApplyDependenciesResult;
    shoppingCartProductsVisibility: IProductVisibility[] = [];
    eventType: EventType;
    showActivitiesListSubject = new Subject<void>();
    showActivitiesCartSubject = new Subject<IGetActivitiesCartResponse>();
    activitiesCartDisplayType = ActivitiesCartDisplayType.listView;
    activitiesCartShowAddToCalendar = false;
    activitiesCartHideAvailablePlacesLeft = false;
    isActivitiesCartRequired = false;
    isActivitiesCartLocked = false;
    hasActivitiesCart = false;
    shoppingCartV2Data: IShoppingCartV2Data | null = null;
    hasShoppingCartV2 = false;

    // myEventsDataSubject = new Subject<ITableData>();
    // myEventsTableFlags: IMyEventsTableFlags = { showInMyTimeVisible: true };
    // myEventsLastResponse: ISearchProfileEventsResponse;
    myEventsTableDateConversion = DateConversion;
    // private myEventsLastTableSettings: ITableSettings;
    myEventsData: IMyEventsData;
    revereCheckoutUrl = '';
    // revereForm: HTMLFormElement;
    revereFormFieldValidation: Record<string, { valid: boolean, message: string }> = {};
    revereDialogConfig: IRevereCheckoutDialogConfig;
    revereCheckoutDialogResult: IRevereCheckoutDialogResult = { proceed: false };
    eventListRestrictToEventId = '';
    isImpersonated: boolean;

    @ViewChild(ShoppingCartV2Component, { static: true }) shoppingCartV2Cmp!: ShoppingCartV2Component;

    queryParams: IIdWithValue[];
    private initJavascriptIntegrationsInitiated = false;
    private eventStartDate: string;
    private eventEndDate: string;
    private eventName: string;
    private linkTokenInfo: ITokenInfo<ILinkTokenPayload>;
    private clearEventFoundInUrl = false;
    private activityCartItems: ICartActivityItem[] = INITIAL_ACTIVITY_CART_ITEMS;
    private _cartItems: ICartItemProduct[];
    /**
     * - if `undefined`, `cartItems` will be recalculated in `computeCartItems()` when reloading the dependencies
     * - if there is no ShoppingCartV2, then `cartItems` will stay `undefined`
     */
    get cartItems() {
        return this._cartItems;
    }
    set cartItems(items: ICartItemProduct[] | undefined) {
        if (!items) {
            this._cartItems = items;
            return;
        }
        // make sure the id of cart items are unique
        const map: Record<string, ICartItemProduct> = {};
        items.forEach(item => map[item.product.id] = item);
        this._cartItems = Object.values(map);
    }
    private getActivitiesCartResponse: IGetActivitiesCartResponse | null = null;

    // Below are Shared API Call to prevent duplicate API call,
    // because these endpoints were called inside `reloadDependencies` which could runs concurrently
    getPaymentProduct$?: Observable<IPaymentProductResponse>;
    getActivitiesCart$?: Observable<IGetActivitiesCartResponse>;
    /** `getPaymentOrders$` only has value if `eventParticipant` has value during initialization */
    getPaymentOrders$?: Observable<IOrder[]>;

    pageIdRedirects: PageIdRedirectMap = {
        'data-profile-subscriptions': 'subscriptions'
    };

    private utmParamValue: Record<string, any> | null = null;
    private originalUtmParamValue: Record<string, any> | null = null;
    private mergeParticipantWithUtmObjectResult: IMergeParticipantWithUtmObjectResult = {
        originalParticipantObject: {} as IObjectWithId, mergedUtmObject: {}, resultingObject: {} as IObjectWithId
    };

    @HostBinding('attr.is-embed') isEmbed = new URLSearchParams(document.location.search)?.get('embed') === 'true';
    contentLanguageElement = this.doc.querySelector<HTMLMetaElement>('[http-equiv="Content-Language"]');

    constructor(
        private readonly appSvc: AppService, private apiSvc: ApiService, private domSvc: AppDomService,
        private readonly loginPageDomSvc: LoginPageDomService, private validatorSvc: ValidatorService,
        private readonly storageSvc: StorageService, private notificationSvc: NotificationsService,
        private readonly modalDialogSvc: ModalDialogService, private utilsSvc: UtilsService, private apiUtilsSvc: ApiUtilsService,
        private readonly domSanitizer: DomSanitizer, private cropperDialogSvc: CropperModalDialogService,
        private readonly emailCodeDialogSvc: EmailCodeModalDialogService, private translateSvc: TranslateService,
        private readonly appLoggerSvc: AppLoggerService,
        private readonly momentSvc: MomentService, private subjectsSvc: SubjectsService,
        private readonly enterEmailDialogSvc: EnterEmailModalDialogService,
        private readonly monerisSvc: MonerisService,
        private readonly myEventsSvc: MyEventsService,
        private readonly sharedSvc: SharedService,
        private readonly revereCheckoutDialogSvc: RevereChechkoutModalDialogService,
        private readonly activityCartListSvc: ActivitiesCartListService,
        private readonly shoppingCartV2Service: ShoppingCartV2Service,
        private readonly profileSubscriptionService: ProfileSubscriptionsService,
        private readonly addToEventDialogSvc: AddToEventModalDialogService,
        @Inject(DOCUMENT) private doc: Document,
        private readonly languageSelectorSvc: LanguageSelectorService,
        private readonly multiRegistrationSvc: MultiRegistrationService,
        private readonly multiRegistrationFormSvc: MultiRegistrationFormService,
        private readonly eventDataSvc: EventDataService,
    ) {
    }

    async ngOnInit(): Promise<void> {
        // First handle special query params which can change global settings
        this.handleUseSessionStorageQueryParam();
        this.handleImpersonationDataQueryParam();

        this.queryParams = this.handleQueryParams();
        this.appSvc.removeSearchParamFromUrl(SearchParamName.utm);
        this.myEventsData = {
            dataSubject: new Subject<ITableData>(),
            rowActionGetter: () => ({}),
            // flags: { showInMyTimeVisible: true },
            dateConversion: DateConversion.objectTime,
            isOrganizationManager: false,
            companyManagerDependencyId: '',
            companyManagerDependencyResult: true,
            disableCompanyContacts: false,
            thisEventOnly: true,
            restrictToEventId: '',
            skipEmailAddressStep: false,
        };
        this.handleUrlFeatureFlags();
        this.translateSvc.setDefaultLang(this.defaultLanguage);
        window.addEventListener('popstate', ev => this.processWindowPopState(ev));
        this.pageData = {} as any;
        this.validatorSvc.setParticipantFieldValidatedCallback(
            (fieldId: string, element: HTMLElement, validationResult: IValidationResult, errorsContainerElementId?: string) =>
                this.fieldValidated(fieldId, element, validationResult, errorsContainerElementId)
        );
        this.eventId = this.appSvc.getEventIdFromUrl();
        this.apiSvc.subscribeToErrorNotifications({
            next: err => this.processApiError(err)
        });

        const initResult = this.apiSvc.init({
            baseUrl: environment.baseUrl,
            contactBaseUrl: environment.contactBaseUrl,
            threeDotOneBaseUrl: environment.threeDotOneBaseUrl,
            paymentIntegrationWebhookBaseUrl: environment.paymentIntegrationWebhookBaseUrl,
            client_id: environment.clientId,
            eventId: this.eventId
        });
        this.storedIdentityToken = initResult.identityToken;
        // this.storedEventTokens = initResult.eventTokens;

        try {
            this.eventSettings = await this.getEventSettings();
            this.domSvc.setEventSettings(this.eventSettings);
            this.domSvc.setFavIcon(`${window.location.origin}/3.0/w/customer/${this.eventSettings.customer_id}/favicon.png`);
            this.momentSvc.setEventSettings(this.eventSettings);
            this.setCurrentLanguage(null);
        } catch (err) {
            // TODO: What if settings cannot be loaded ?
        }

        this.sideMenuItems = [];
        this.sideMenuItems.push({ id: SideMenuItemId.myProfile, text: this.appSvc.translate(TranslationKey.myProfile) });
        // this.sideMenuItems.push({ id: SideMenuItemId.myEvents, text: this.appSvc.translate(TranslationKey.myEvents) });
        this.sideMenuItems.push({ id: SideMenuItemId.events, text: this.appSvc.translate(TranslationKey.events) });
        this.sideMenuItems.push({ id: SideMenuItemId.requests, text: this.appSvc.translate(TranslationKey.requests) });
        this.sideMenuItems.push({ id: SideMenuItemId.myOrders, text: this.appSvc.translate(TranslationKey.myOrders) });
        this.sideMenuItems.push({ id: SideMenuItemId.myOrganization, text: this.appSvc.translate(TranslationKey.myOrganization) });
        this.sideMenuItems.push({ id: SideMenuItemId.changePassword, text: this.appSvc.translate(TranslationKey.changePassword) });
        this.sideMenuItems.push({ id: SideMenuItemId.subscriptions, text: this.appSvc.translate(TranslationKey.subscriptions) });
        this.sideMenuItems.push({ id: SideMenuItemId.logout, text: this.appSvc.translate(TranslationKey.logout) });

        // Remove current event token and identity token if "clear=true" is found in the URL
        // Remove current event token if "clear=event" is found in the URL
        this.removeTokensOnClearParam();

        const linkToken = this.appSvc.getSearchParamFromUrl(SearchParamName.token);

        if (!linkToken) {
            await this.startWithoutLinkToken();
        } else {
            await this.startWithLinkToken(linkToken);
        }

        this.manageProfileButtonsVisibility();
    }

    removeTokensOnClearParam(): void {
        const clearTokenStr = this.appSvc.getSearchParamFromUrl(SearchParamName.clear);
        this.appSvc.removeSearchParamFromUrl(SearchParamName.clear);
        if (clearTokenStr === SearchParamValue[SearchParamName.clear].true) {
            // Remove both event and identity tokens
            this.apiSvc.removeStoredIdentityToken();
            this.apiSvc.removeStoredEventToken(this.eventId);
        } else if (clearTokenStr === SearchParamValue[SearchParamName.clear].event) {
            // Remove only event token
            this.apiSvc.removeStoredEventToken(this.eventId);
            this.clearEventFoundInUrl = true;
        }
    }

    handleUseSessionStorageQueryParam(): void {
        const useSessionStorageUrlParamValue = this.appSvc.getSearchParamFromUrl(SearchParamName.useSessionStorage);
        const useSessionStorageStorageParamValue = window.sessionStorage.getItem(SearchParamName.useSessionStorage);
        if (useSessionStorageUrlParamValue || useSessionStorageStorageParamValue) {
            const trueString = 'true';
            // We are not going to remove the parameter so if the tab is refreshed, it will still use the session storage instead of local storage
            if (useSessionStorageUrlParamValue === trueString || useSessionStorageStorageParamValue === trueString) {
                // Also set the value in the session storage itself so even if the URL parameter is removed, the app will still use session storage
                window.sessionStorage.setItem(SearchParamName.useSessionStorage, trueString);
                this.apiSvc.setStorage(window.sessionStorage);
                this.storageSvc.setPermanentStorage(window.sessionStorage);
            }
        }
    }

    handleImpersonationDataQueryParam(): void {
        // We will keep impersonated query parameter so after refresh it is still available for the app
        this.isImpersonated = this.appSvc.getSearchParamFromUrl(SearchParamName.impersonated) === 'true';
        // Apply impersonation token if provided
        const impersonationDataUrlParamValue = this.appSvc.getSearchParamFromUrl(SearchParamName.identity);
        if (impersonationDataUrlParamValue) {
            this.appSvc.removeSearchParamFromUrl(SearchParamName.identity);
            try {
                const base64Decoded = this.apiUtilsSvc.base64Decode(impersonationDataUrlParamValue);
                const impersonationData = JSON.parse(base64Decoded) as IStoredIdentityToken;
                this.appLoggerSvc.log(LogLevel.debug, impersonationData);
                if (impersonationData.email && impersonationData.tokenResponse?.access_token) {
                    // Save the impersonation token data in the storage so it is used from now on
                    this.apiSvc.saveStoredIdentityTokenToStorage(impersonationData);
                } else {
                    this.appLoggerSvc.log(LogLevel.error, `Invalid impersonation token data`, impersonationData);
                }
            } catch (err) {
                this.appLoggerSvc.log(LogLevel.error, `Cannot base64 decode or JSON.stringify the ${SearchParamName.identity} parameter`, impersonationDataUrlParamValue, err);
            }
        }
    }

    handleQueryParams(): IIdWithValue[] {
        const queryParamsQueryParamsKey = 'query-params';
        // Merge URL query params to session storage params
        const storageQueryParams = (this.storageSvc.getSessionItem(queryParamsQueryParamsKey) as IIdWithValue[]) || [];
        const urlQueryParams = this.appSvc.getAllSearchParams();
        for (const urlParam of urlQueryParams) {
            const storageParam = storageQueryParams.find(x => x.id === urlParam.id);
            if (storageParam) {
                storageParam.value = urlParam.value;
            } else {
                storageQueryParams.push(urlParam);
            }
        }
        const utmParam = storageQueryParams.find(x => x.id === SearchParamName.utm);
        if (utmParam) {
            try {
                const base64Decoded = this.apiUtilsSvc.base64Decode(utmParam.value);
                this.originalUtmParamValue = JSON.parse(base64Decoded);
            } catch (e) {
                this.appLoggerSvc.log(LogLevel.error, 'Cannot base64 decode the utm parameter', utmParam.value, e);
            }
        }
        this.storageSvc.setSessionItem(queryParamsQueryParamsKey, storageQueryParams);
        return storageQueryParams;
    }

    async setCurrentLanguage(preferredLanguage: string | null): Promise<void> {
        this.currentLanguage = this.languageSelectorSvc.getLanguage(
            this.eventSettings.languages,
            this.storageSvc.getLanguage(),
            preferredLanguage
        );
        await Promise.all([
            firstValueFrom(this.translateSvc.use(this.currentLanguage)),
            this.loadLanguageDependency(),
            (async() => {
                this.domSvc.setDocumentLanguageAndDirectionAttributes(this.currentLanguage);
            })(),
        ]).catch(() => null);
    }

    private async loadLanguageDependency() {
        this.momentSvc.updateMomentLocale(this.currentLanguage);
    }

    handleUrlFeatureFlags(): void {
        const featureFlagProfile = this.appSvc.getSearchParamFromUrl(SearchParamName.featureFlagProfile);
        if (featureFlagProfile !== null) {
            // this.featureFlags.profileEnabled = featureFlagProfile === 'true';
            this.appSvc.removeSearchParamFromUrl(SearchParamName.featureFlagProfile);
        }
    }

    manageProfileButtonsVisibility(): void {
        const eventTokenPayload = this.apiSvc.getCurrentEventAccessTokenPayload();
        if (eventTokenPayload) {
            // TODO: Can't find contact_id in the token, only participant_id is set by the server
            if (eventTokenPayload.participant_id || eventTokenPayload.contact_id || eventTokenPayload.username) {
                // Logged in - show profile button
                this.profileFlags.showProfileButton = true; // && this.featureFlags.profileEnabled;
                this.profileFlags.showLoginButton = false;
                this.domSvc.changeProfileButtonsVisibility(this.profileFlags.showLoginButton, this.profileFlags.showProfileButton);
            } else {
                // Not logged in - show log in button
                this.profileFlags.showProfileButton = false;
                this.profileFlags.showLoginButton = true; // && this.featureFlags.profileEnabled;
                this.domSvc.changeProfileButtonsVisibility(this.profileFlags.showLoginButton, this.profileFlags.showProfileButton);
            }
        }
    }

    async initJavascriptIntegrations(isInitialLoad?: boolean): Promise<void> {
        if (this.initJavascriptIntegrationsInitiated) {
            return;
        }
        this.initJavascriptIntegrationsInitiated = true;
        this.javascriptIntegrations = await this.apiSvc.getAllScripts();
        if (!this.javascriptIntegrations) {
            return;
        }
        const promises = [];
        for (const jsInteg of this.javascriptIntegrations) {
            let script: string;
            let waitForScriptId: string;
            if (isInitialLoad) {
                if (jsInteg.settings && jsInteg.automation_type === IntegrationType.tealium) {
                    waitForScriptId = 'tealium_load_utag';
                    const replacedAccountName = tealiumSnippet.replace('TEALIUM-ACCOUNT-NAME-PLACEHOLDER', jsInteg.settings.account_name);
                    const replacedProfile = replacedAccountName.replace('TEALIUM-PROFILE-PLACEHOLDER', jsInteg.settings.profile);
                    script = replacedProfile.replace('TEALIUM-ENVIRONMENT-PLACEHOLDER', jsInteg.settings.environment);
                }
                if (jsInteg.settings && jsInteg.automation_type === IntegrationType.googleTagManager) {
                    script = googleTagManagerSnippet.replace('GTM-ID-PLACEHOLDER', jsInteg.settings.gtm);
                }
                if (jsInteg.settings && jsInteg.automation_type === IntegrationType.googleAnalytics) {
                    this.domSvc.insertGoogleAnalyticsMetaTag(jsInteg.settings.ga);
                    script = googleAnalyticsSnippet.replace('G-ID-PLACEHOLDER', jsInteg.settings.ga);
                }
            }
            if (jsInteg.settings && jsInteg.automation_type === IntegrationType.javascript) {
                script = jsInteg.settings.script;
            }
            if (jsInteg.settings && jsInteg.automation_type === IntegrationType.custom) {
                script = jsInteg.settings.script;
            }
            if (script) {
                const isRegularPageLoad = !isInitialLoad;
                promises.push(this.domSvc.loadJavascriptIntegration(jsInteg.id, script, isRegularPageLoad, waitForScriptId));
            }
        }
        await Promise.all(promises);
        this.initJavascriptIntegrationsInitiated = true;
    }

    private async startWithLinkToken(linkToken: string): Promise<void> {
        try {
            // We have link token - obtain identity token
            this.linkTokenInfo = this.apiSvc.getLinkTokenInfo(linkToken);
            const getIdentityTokenResponse = await this.apiSvc.getIdentityTokenFromLinkToken(linkToken);
            this.username = getIdentityTokenResponse.token.username;
            this.appSvc.removeSearchParamFromUrl(SearchParamName.token);
        } catch (err) {
            const apiErr = err as ApiError;
            if (apiErr.communicationError
                && apiErr.communicationError.body
                && apiErr.communicationError.body.code === 'NewLinkSent') {
                // This token was used more than one time
                this.domSvc.setErrorHeaderContent(this.appSvc.translate(TranslationKey.suppliedCodeIsNoLongerValid));
            } else {
                if ((err as Error).message === 'ERROR:InvalidToken') {
                    this.domSvc.setErrorHeaderContent(this.appSvc.translate(TranslationKey.invalidToken));
                } else {
                    // TODO: Some unknown error occured
                    this.domSvc.setErrorHeaderContent(this.appSvc.translate(TranslationKey.unknownErrorOccured));
                }
            }
            return;
        }
        await this.startupFlowUseIdentityTokenToGetEventToken();
    }

    async startupFlowCheckIdentityTokenExistence(): Promise<void> {
        const storedIdentityToken = this.apiSvc.getStoredIdentityToken();
        // const storedEventToken = this.apiSvc.getStoredEventToken(this.eventId);
        if (storedIdentityToken) {
            // Get event token from identity token
            await this.startupFlowUseIdentityTokenToGetEventToken();
        } else {
            // No identity token - get public event token
            await this.startupFlowProcessPublicEvent();
            // await this.startupFlowCheckIsEventPublic();
        }
        // if (!storedIdentityToken) {
        //     // We don't have identity token - check if we have event token
        //     if (!storedEventToken) {
        //         // No event token - try to start public event flow
        //         await this.startupFlowCheckIsEventPublic();
        //     } else {
        //         // We have event token - get new event token with it
        //         await this.startupFlowUseEventTokenToGetEventToken();
        //     }
        // } else {
        //     // We have identity token - check if the event token matches identity token
        //     if (this.eventTokenHasParticipantIdAndIdentityTokenUsernameIsTheSameAsInEventToken(storedIdentityToken, storedEventToken)) {
        //         // Event token has participant_id and identity token and event token are for the same username
        //         await this.startupFlowUseEventTokenToGetEventToken();
        //     } else {
        //         // Event token does not have participant_id or identity token and event token username does not match
        //         await this.startupFlowUseIdentityTokenToGetEventToken();
        //     }
        // }
    }

    async startupFlowUseIdentityTokenToGetEventToken(): Promise<void> {
        await this.tokenGetEventTokenUsingCurrentIdentityToken();
        await this.startupFlowDetermineNextPageIdAndLoad();
    }

    // async startupFlowUseEventTokenToGetEventToken(): Promise<void> {
    //     await this.tokenGetEventTokenUsingCurrentEventToken(this.eventId);
    //     await this.startupFlowDetermineNextPageIdAndLoad();
    // }

    async startupFlowDetermineNextPageIdAndLoad(): Promise<void> {
        let pageId: string;
        // Check if we are returning from payment
        const transactionStatusStr = this.appSvc.getSearchParamFromUrl(SearchParamName.transaction_status);
        if (transactionStatusStr && this.eventTokenHasParticipantId()) {
            const thankYouPageRes = await this.apiSvc.getPaymentThankYouPage(transactionStatusStr);
            pageId = thankYouPageRes.next_page_id;
            this.appSvc.removeSearchParamFromUrl(SearchParamName.transaction_status);
        }
        pageId ||= this.appSvc.getSearchParamFromUrl(SearchParamName.page_id);
        await this.loadEventPage(pageId, true);
    }

    private async tokenGetEventTokenUsingCurrentIdentityToken(): Promise<IGetEventTokenResponse> {
        const eventTokenResponse = await this.apiSvc.getEventTokenUsingCurrentIdentityToken(this.eventId, this.linkTokenInfo?.payload?.entity_id, this.clearEventFoundInUrl);
        // We also need to call another endpoint which will eventually put profile information in the token
        await this.loadAndStoreProfilePage();
        return eventTokenResponse;
    }

    private async tokenGetEventPublicToken(): Promise<IGetEventTokenResponse> {
        const result = await this.apiSvc.getEventPublicToken(this.eventId);
        await this.loadAndStoreProfilePage();
        return result;
    }

    private async tokenRefreshEventToken(): Promise<IGetEventTokenResponse> {
        const eventTokenResponse = await this.apiSvc.refreshEventToken(this.eventId);
        await this.loadAndStoreProfilePage();
        return eventTokenResponse;
    }

    private async getEventTokenUsingCurrentIdentityToken(eventId: string): Promise<IGetEventTokenResponse> {
        const identityToken = this.apiSvc.getStoredIdentityToken();
        if (!identityToken) {
            return null;
        }
        const eventTokenInfo = this.apiSvc.getStoredEventToken(this.eventId);
        if (!eventTokenInfo) {
            return null;
        }
        const eventTokenPayload = this.apiUtilsSvc.getTokenInfo<IEventTokenPayload>(eventTokenInfo.tokenResponse.access_token);
        const currentUserId = eventTokenPayload?.payload?.participant_id || eventTokenPayload?.payload?.contact_id;
        if (!currentUserId) {
            return null;
        }
        const res = await this.apiSvc.getEventTokenUsingCurrentIdentityToken(eventId, currentUserId, false);
        return res;
    }

    private async tokenGetEventTokenUsingCurrentEventToken(eventId: string): Promise<IGetEventTokenResponse> {
        const res = await this.apiSvc.getEventTokenUsingCurrentEventToken(eventId, this.linkTokenInfo?.payload?.entity_id, this.clearEventFoundInUrl);
        await this.loadAndStoreProfilePage();
        return res;
    }

    private async tokenGetEventToken(): Promise<IGetEventTokenResponse> {
        const participantIdFromLinkToken = this.linkTokenInfo?.payload?.entity_id;
        const response = await this.apiSvc.getEventToken(this.eventId, participantIdFromLinkToken, this.clearEventFoundInUrl);
        await this.loadAndStoreProfilePage();
        this.setEventTokenReponse(response);
        return response;
    }

    private async loadAndStoreProfilePage(): Promise<IProfilePageResponse> {
        // Getting profile page causes the server to put something in the token - we need to call this after get/refresh token endpoints
        this.profilePageResponse = await this.apiSvc.getProfilePage();
        return this.profilePageResponse;
    }

    eventTokenHasParticipantId(): boolean {
        const eventTokenInfo = this.getCurrentEventTokenInfo();
        return !!eventTokenInfo?.payload?.participant_id;
    }

    eventTokenHasContactId(): boolean {
        const eventTokenInfo = this.getCurrentEventTokenInfo();
        return !!eventTokenInfo?.payload?.contact_id;
    }

    getCanShowMyEvents(): boolean {
        const eventTokenInfo = this.getCurrentEventTokenInfo();
        return !!eventTokenInfo?.payload?.contact_id || !!eventTokenInfo?.payload?.username;
    }

    eventTokenIsOrganizationManager(): boolean {
        const eventTokenInfo = this.getCurrentEventTokenInfo();
        return !!eventTokenInfo?.payload?.om;
    }

    getCurrentEventTokenInfo(): ITokenInfo<IEventTokenPayload> {
        const storedEventToken = this.apiSvc.getStoredEventToken(this.eventId);
        const eventTokenInfo = this.apiSvc.getEventTokenInfo(storedEventToken);
        return eventTokenInfo;
    }

    eventTokenHasParticipantIdAndIdentityTokenUsernameIsTheSameAsInEventToken(
        storedIdentityToken: IStoredIdentityToken, storedEventToken: IStoredEventToken
    ): boolean {
        if (!storedIdentityToken || !storedEventToken) {
            return false;
        }
        const identityTokenInfo = this.apiSvc.getIdentityTokenInfo(storedIdentityToken);
        if (!identityTokenInfo) {
            return false;
        }
        const eventTokenInfo = this.apiSvc.getEventTokenInfo(storedEventToken);
        if (!eventTokenInfo) {
            return false;
        }
        if (eventTokenInfo.payload.participant_id && identityTokenInfo.payload.username === eventTokenInfo.payload.username) {
            return true;
        } else {
            return false;
        }
    }

    async startWithoutLinkToken(): Promise<void> {
        // URL does not contain token
        const identityToken = this.apiSvc.getStoredIdentityToken();
        if (identityToken) {
            // If we already have identity token use it
            await this.startupFlowCheckIdentityTokenExistence();
        } else {
            // We don't have identity token - see if we have event token
            const currentEventToken = this.apiSvc.getStoredEventToken(this.eventId);
            if (currentEventToken) {
                // We have event token in the storage
                this.apiSvc.setEventTokenResponse(this.eventId, currentEventToken.tokenResponse);
                // Extract email from event token so we can logout later and remove the identity token for this email
                this.email = this.apiSvc.getUsernameFromEventToken(currentEventToken.tokenResponse.access_token);
                await this.startupFlowRefreshCurrentEventTokenAndDetermineNextPageIdAndLoad();
            } else {
                // No event token and no identity token - start with public event
                await this.startupFlowProcessPublicEvent();
            }
        }
    }

    async startupFlowRefreshCurrentEventTokenAndDetermineNextPageIdAndLoad(): Promise<void> {
        const eventTokenResponse = await this.tokenRefreshEventToken();
        this.setEventTokenReponse(eventTokenResponse);
        await this.startupFlowDetermineNextPageIdAndLoad();
    }

    // async startupFlowCheckIsEventPublic(): Promise<void> {
    //     const isEventAccessPublic = this.eventSettings.access === 'public';
    //     if (isEventAccessPublic) {
    //         await this.startupFlowProcessPublicEvent();
    //     } else {
    //         this.startupFlowProcessNotPublicEvent();
    //     }
    // }

    showAccessDeniedPage(): void {
        this.domSvc.setErrorHeaderContent(this.appSvc.translate(TranslationKey.accessDenied));
    }

    async startupFlowProcessPublicEvent(): Promise<void> {
        const getEventPublicTokenResponse = await this.tokenGetEventPublicToken();
        this.username = getEventPublicTokenResponse.token.username;
        await this.startupFlowDetermineNextPageIdAndLoad();
    }

    startupFlowProcessNotPublicEvent(): void {
        this.showAccessDeniedPage();
    }

    processWindowPopState(ev: PopStateEvent): void {
        const pageId = this.appSvc.getSearchParamFromUrl(SearchParamName.page_id);

        if (pageId !== this.pageData?.id) { // Prevent reloading the same page when opening an anchor link
            this.loadEventPage(pageId);
        }
    }

    setEventTokenReponse(response: IGetEventTokenResponse): void {
        this.eventTokenResponse = response;
    }

    async getEventPublicToken(): Promise<void> {
        this.eventTokenResponse = await this.tokenGetEventPublicToken();
    }

    async getEventSettings(): Promise<IEventSettings> {
        const e = this.eventId;
        const settingsResponse = await this.apiSvc.getEventSettings(this.eventId);
        return settingsResponse;
    }

    async getEventParticipantFields(): Promise<IField[]> {
        if (this.eventParticipantFields) {
            return this.eventParticipantFields;
        }
        const eventParticipantFields = await this.apiSvc.getAllEventParticipantFields();
        this.eventParticipantFields = this.appSvc.getModifiedEventParticipantFields(
            eventParticipantFields, this.eventSettings.languages
        );
        return this.eventParticipantFields;
    }

    async getEventFields(): Promise<IField[]> {
        if (this.eventFields) {
            return this.eventFields;
        }
        this.eventFields = await this.apiSvc.getAllEventFields();
        return this.eventFields;
    }

    async getEventMenuItems(): Promise<IMenuItem[]> {
        this.eventMenuItems = await this.apiSvc.getMenu();
        return this.eventMenuItems;
    }

    async loadEventHomePage(initialLoad?: boolean): Promise<void> {
        this.loadEventPage('', initialLoad);
    }

    setFormMenuPageId(menuItems: IMenuItem[]): void {
        const formMenuItem = menuItems.find(x => !x.page_id && x.type === 'aza-form');
        if (formMenuItem) {
            formMenuItem.page_id = this.processFormResponse.form_id;
        }
    }

    private isProfilePageToOpen(pageId: string): boolean {
        if (pageId && pageId.startsWith('data-profile')) {
            return true;
        }
        return false;
    }

    async loadEventPage(pageId?: string, initialLoad?: boolean): Promise<void> {
        if (this.pageIdRedirects[pageId]) {
            return this.loadEventPage(this.pageIdRedirects[pageId], initialLoad);
        }
        if (this.isProfilePageToOpen(pageId)) {
            // Reload with removed page id so the home page is opened and add search param to the URL
            const uri = new URL(window.location.href);
            uri.searchParams.set(SearchParamName.page_id, '');
            uri.searchParams.set(SearchParamName.openProfilePage, pageId);
            window.location.href = uri.href;
            return;
        }
        const declineLinkFromUrl = this.appSvc.getSearchParamFromUrl(SearchParamName.decline);
        let isDeclineInUrl = false;
        if (declineLinkFromUrl !== null) {
            this.appSvc.removeSearchParamFromUrl(SearchParamName.decline);
            isDeclineInUrl = true;
        }
        let processFormQueryParams = this.getProfilePageIdQueryParams();
        if (pageId) {
            processFormQueryParams ||= {};
            processFormQueryParams[SearchParamName.page_id] = pageId;
        }
        const processFormResponse = await this.apiSvc.getProcessForm(isDeclineInUrl, processFormQueryParams);
        if (isDeclineInUrl) {
            pageId = processFormResponse.form_id;
        }
        // If we have page_id=aza-form in the URL - load the form in processFormResponse.form_id instead
        const pageIdFromUrl = this.appSvc.getSearchParamFromUrl(SearchParamName.page_id);
        if (pageIdFromUrl === SpecialPageId.azaForm) {
            // Remove tha page_id from the URL so we don't enter infinite loop
            this.appSvc.removeSearchParamFromUrl(SearchParamName.page_id);
            pageId = processFormResponse.form_id;
        }
        const pageData = pageId ? await this.apiSvc.getEventPage(pageId) : await this.apiSvc.getEventHomePage().catch(() => null);
        await this.setCurrentLanguage(pageData?.participant?.preferred_language);
        if (isDeclineInUrl && !pageId) {
            // show confirmation popup
            const dialogConfig: IModalDialogConfig = {
                title: '',
                textLines: [this.appSvc.translate(TRANSLATION_MAP.DO_YOU_WANT_TO_DECLINE_INVITATION)],
                firstButton: { id: 'back', text: this.appSvc.translate(TRANSLATION_MAP.NO) },
                secondButton: { id: 'ok', text: this.appSvc.translate(TRANSLATION_MAP.YES_DECLINE) }
            };
            const dialogResult = await this.modalDialogSvc.showPopupDialog(dialogConfig);
            this.modalDialogSvc.hidePopupDialog();
            if (dialogResult.clickedButtonId === 'ok') {
                const completeProcessFormResponse = await this.apiSvc.completeProcessForm('aza-form', { decline: 1 });
                return this.navigateToCompleteResponseNextPage(completeProcessFormResponse);
            } else {
                const newTokenForHomePage = (await this.apiSvc.getProcessForm(false, {}));
                this.apiSvc.processTokenResponse(this.storedIdentityToken.email, newTokenForHomePage.token);
            }
        }
        if (!pageData) {
            // TODO: This page does not exist or some other error occured
            this.notificationSvc.showNotification(NotificationMessageType.error, this.appSvc.translate(TranslationKey.cannotLoadPage) + ` (${pageId || ''})`);
            return;
        }
        this.eventParticipantFields = await this.getEventParticipantFields();
        // Convert crm field ids in the original utm param value to event participant field ids
        this.utmParamValue = this.appSvc.getUtmParamValueWithSubstitutedFieldIds(this.eventParticipantFields, this.originalUtmParamValue);
        this.utilsSvc.setTravelDateFieldsTimezone(this.eventParticipantFields);
        this.eventFields = await this.getEventFields();
        this.utilsSvc.setTravelDateFieldsTimezone(this.eventFields);
        this.errorFormFields = {};
        this.pageData = pageData;
        const eventStartDateFieldId = this.eventFields.find(x => x.name === 'start')?.id;
        const eventEndDateFieldId = this.eventFields.find(x => x.name === 'end')?.id;
        const eventNameFieldId = this.eventFields.find(x => x.name === 'name')?.id;
        this.eventStartDate = this.pageData.event[eventStartDateFieldId] || this.eventSettings.start;
        this.eventEndDate = this.pageData.event[eventEndDateFieldId] || this.eventSettings.end;
        this.eventName = this.pageData.event[eventNameFieldId] || this.eventSettings.name;
        this.processFormResponse = processFormResponse;
        // Use the new dependency structure returned by the server
        this.eventDependencies = this.pageData.dependencies;
        this.mergeParticipantWithUtmObjectResult = this.appSvc.mergeParticipantWithUtmObject(this.pageData.participant, this.utmParamValue);
        this.eventParticipant = this.mergeParticipantWithUtmObjectResult.resultingObject;
        this.updatedEventParticipant = {};
        this.getPaymentOrders$ = scheduled(
            this.eventParticipant?.id ? this.apiSvc.getPaymentOrders() : Promise.resolve<undefined>(undefined),
            asapScheduler
        );
        this.getPaymentProduct$ = scheduled(this.apiSvc.getPaymentProduct(), asapScheduler);
        this.getActivitiesCart$ = scheduled(this.apiSvc.getActivitiesCart(), asapScheduler);

        this.updateFromMultiSelectFields(this.eventParticipantFields, this.eventParticipant);

        this.validatorSvc.setFields(this.eventParticipantFields, this.eventParticipant);
        // If we have profile page data we could fill the missing participant field values like first_name, last_name and email
        this.appSvc.setMissingParticipantFields(this.eventParticipant, this.eventParticipantFields, this.profilePageResponse);
        if (this.eventParticipant) {
            // Add utm codes if matches are found
            const utmCodes = this.getEventParticipantUtmCodes();
            if (utmCodes.length > 0) {
                // We have match(es) between utm codes from url and participant fields - set them to the payload
                for (const utmCode of utmCodes) {
                    this.eventParticipant[utmCode.id] = utmCode.value;
                }
            }
        }

        // reset cartItems on page changes
        this.cartItems = undefined;

        // If there are 2 or more "aza-form" in the menu items, leave only this which have the same id
        // as the result in getProcessForm
        const azaFormMenuItems = this.pageData.menu.filter(x => x.type === 'aza-form');
        if (azaFormMenuItems.length > 1) {
            this.pageData.menu = this.pageData.menu.filter(x => x.type !== 'aza-form' || x.page_id === this.processFormResponse.form_id);
        }
        this.eventMenuItems = this.pageData.menu;
        this.setFormMenuPageId(this.eventMenuItems);
        const setEventPageDataResult = await this.setEventPageData(this.pageData);
        this.currentLanguage = this.languageSelectorSvc.getLanguage(
            this.eventSettings.languages,
            this.storageSvc.getLanguage(),
            this.pageData.participant?.preferred_language
        );
        this.domSvc.setSelectElementsOptions(this.eventParticipantFields, this.currentLanguage);
        this.domSvc.setLanguageItemsToLanguageDropDowns(this.eventSettings.languages, lang => this.languageChanged(lang, true));
        this.languageChanged(this.currentLanguage, false);
        if (!this.eventParticipant) {
            // This is new event participant - try to find its email field and set its value according to what was specified in sign in page
            const emailField = this.appSvc.getEmailField(this.eventParticipantFields);
            if (emailField) {
                this.domSvc.setAllInputElementsValue(emailField, this.email);
            }
        }
        const preferredLanguageField = this.appSvc.getPreferredLanguageField(this.eventParticipantFields);
        if (preferredLanguageField) {
            this.domSvc.setPreferredLanguageSelectOptions(preferredLanguageField, this.eventSettings.languages);
        }
        this.domSvc.fillInputElements(this.eventParticipant, this.eventParticipantFields);
        this.domSvc.renderOpinionScaleFields();
        this.domSvc.renderRatingFields();

        // We must show the activities cart after the fields are filled so registered activities are correctly
        // calculated if they are attached to dependencies
        this.showActivitiesCartSubject.next(this.getActivitiesCartResponse);

        this.validateAllFields(this.eventParticipantFields);
        if (this.eventParticipant.id) {
            this.domSvc.disableAllEmailInputElements(
                this.eventParticipantFields
            );
        }

        // Process shopping cart V2
        if (this.hasShoppingCartV2) {
            this.productResponse = await firstValueFrom(this.getPaymentProduct$);
            if (this.eventParticipant?.id) {
                this.orders = await firstValueFrom(this.getPaymentOrders$);
            }
            this.domSvc.moveShoppingCartV2ComponentElementToDynamicShoppingCartElementPlaceholder();
            const shouldShowDiscountCodeField = this.domSvc.shouldShowShoppingCartV2DiscountInput();
            this.shoppingCartV2Data = await this.createShoppingCartV2Data(shouldShowDiscountCodeField);
        } else {
            this.shoppingCartV2Data = null;
        }

        const hasMultiRegistrationSection = !!this.domSvc.getSectionMultiRegistration();
        if (hasMultiRegistrationSection) {
            this.domSvc.moveMultiRegistrationComponentElementToDynamicElementPlaceholder();
        }

        await this.reloadDependencies(true);
        // Attach to submit button if exists
        const submitButtons = this.domSvc.getSubmitButtons();
        const disabledClassName = 'disabled';
        submitButtons.forEach(btn => {
            btn.onclick = (async (ev: Event) => {
                submitButtons.forEach(submitBtn => {
                    this.domSvc.addClass(submitBtn, this.loadingButtonClassName);
                    this.domSvc.addAttribute(submitBtn, disabledClassName, '');
                });
                try {
                    await this.submitForm();
                } finally {
                    submitButtons.forEach(submitBtn => {
                        this.domSvc.removeClass(submitBtn, this.loadingButtonClassName);
                        this.domSvc.removeAttribute(submitBtn, disabledClassName);
                    });
                }
            }).bind(this);
        });
        const cancellationBtn = this.domSvc.getCancellationButton();
        if (cancellationBtn) {
            cancellationBtn.onclick = ((ev: Event) => {
                ev.preventDefault();
                ev.stopPropagation();
                this.processCancellationButtonClicked();
            }).bind(this);
        }
        if (this.shouldShowYourFormHasBeenSubmittedMessage) {
            this.domSvc.showTextInMessageElements(this.appSvc.translate(TranslationKey.yourFormHasBeenSubmitted));
            this.shouldShowYourFormHasBeenSubmittedMessage = false;
        }
        this.appSvc.storeSearchParamInUrl(SearchParamName.page_id, this.pageData.id);
        this.domSvc.setPageTitle(pageData.name);
        this.domSvc.setAttributeForAnchorElements();

        // Check if we have open-profile-page search param
        const openProfilePageSearchParamValue = this.appSvc.getSearchParamFromUrl(SearchParamName.openProfilePage);
        if (openProfilePageSearchParamValue) {
            this.appSvc.removeSearchParamFromUrl(SearchParamName.openProfilePage);
            this.showProfilePage(openProfilePageSearchParamValue);
        }

        this.processSpecialPage();

        try {
            this.initJavascriptIntegrations(initialLoad);
            this.triggerIntegrations();
        } catch (e) {
            this.appLoggerSvc.log(LogLevel.error, 'Loading javascript integrations failed', e);
        }

        const shouldInitMultiRegistration = this.processFormResponse.multiregistration?.workflow_id && this.domSvc.getSectionMultiRegistration();
        if (shouldInitMultiRegistration) {
            this.multiRegistrationSvc.initController(
                this.eventId,
                {
                    ...processFormResponse.multiregistration,
                },
                () => this.getShoppingCartResultUI(),
            );
        } else {
            this.multiRegistrationSvc.destroy();
        }
    }

    /**
     *  This methods will reload the dependencies-cycle in the app,
     *
     * and will safely optimize the rerendering by only rerender if the `dependencyResult` changes.
     * */
    private async reloadDependencies(forceReload?: boolean) {
        // Prepare dependency activities and activityCartItems if its empty
        if (this.hasActivitiesCart || hasDependencyActivities(this.eventDependencies ?? [])) {
            if (this.getActivitiesCartResponse == null) {
                this.getActivitiesCartResponse = await firstValueFrom(this.getActivitiesCart$);
            }
            if (this.activityCartItems && this.activityCartItems.length === 0) {
                const registeredActivitiesIds = this.activityCartListSvc.getRegisteredActivitiesFromServerResponse(
                    this.getActivitiesCartResponse.activities, this.applyDependenciesResult?.dependencyResults, null
                );
                this.activityCartItems = this.activityCartListSvc.convertToCartActivityItems(
                    this.getActivitiesCartResponse,
                    this.applyDependenciesResult?.dependencyResults,
                    registeredActivitiesIds
                );
            }
        }
        // Compute initial cartItems if its still empty
        await this.computeCartItems();

        this.appLoggerSvc.group('// Evaluate Dependency logs'); // The next appLoggerSvc.log is placed inside the AppDomService.applyDependencies()

        const dependencyResult = this.domSvc.applyDependencies(
            this.eventParticipantFields,
            this.eventDependencies ?? [],
            this.eventParticipant,
            Object.keys(this.croppedImagesMap),
            Object.keys(this.deletedImageFieldValuesMap),
            Object.keys(this.documentFieldsFilesMap),
            Object.keys(this.deletedDocumentFieldsFilesMap),
            this.pageData.event,
            this.activityCartItems.map(item => item.cartActivity) ?? [],
            this.getShoppingCartResultUI(),
            this.getWorkflowDependency(),
            this.eventSettings as EventSettings,
        );
        const getMatchedDependencyIds = (result: IApplyDependenciesResult) =>
            result?.dependencyResults?.
                filter(({ result }) => result).
                map(({ dependency }) => dependency.id) ?? [];

        // Stop execution if the dependencyResult's ids are same, because it could potentially leads to infinite loop
        if (!forceReload && this.applyDependenciesResult && isContainingSameMember(
            getMatchedDependencyIds(dependencyResult), getMatchedDependencyIds(this.applyDependenciesResult)
        )) {
            this.appLoggerSvc.log(
                LogLevel.log,
                '%c// Evaluate Dependency logs: Dependencies are skipped because it doesn\'t change',
                'color: black; font-style: italic; background-color: yellow;padding: 2px',
                getMatchedDependencyIds(dependencyResult)
            );
            this.appLoggerSvc.groupEnd();
            return;
        }
        this.applyDependenciesResult = dependencyResult;
        this.hiddenFieldIds = this.applyDependenciesResult.hiddenFieldIds;
        // Use result to show/hide shopping cart products
        // This will set this.shoppingCartProductsVisibility
        this.changeProductsVisibilityByDependencies(
            this.applyDependenciesResult
        );

        // Use result to show/hide activity cart items
        this.changeActivityCartItemsVisibilityByDependencies(this.applyDependenciesResult);

        this.updateMyEventsListAfterDependencyUpdate();

        this.appLoggerSvc.log(
            LogLevel.log,
            '%c// Evaluate Dependency logs: Dependencies are reloaded',
            'color: yellow; font-style: italic; background-color: green;padding: 2px',
            getMatchedDependencyIds(dependencyResult)
        );
        this.appLoggerSvc.groupEnd();
    }

    private getShoppingCartResultUI() {
        return this.shoppingCartV2Service.process({
            cartItems: this.cartItems?.map(getCartItemFromCartItemProduct) ?? [],
            discount: this.lastShoppingCartChangedData?.discount,
            pay_by_invoice: this.productResponse?.pay_by_invoice,
        });
    }

    private async computeCartItems() {
        if (this.hasShoppingCartV2 || hasDependencyProducts(this.eventDependencies ?? [])) {
            if (!this.orders) {
                this.orders = await firstValueFrom(this.getPaymentOrders$);
                this.order = this.orders[0];
            }
            if (!this.productResponse) {
                this.productResponse = await firstValueFrom(this.getPaymentProduct$);
            }
            if (!this.shoppingCartV2Service.processor) {
                this.shoppingCartV2Service.init(
                    await this.createShoppingCartV2Data(this.domSvc.shouldShowShoppingCartV2DiscountInput())
                );
            }
            this.cartItems = this.cartItems ?? this.shoppingCartV2Service.computeCartItems(this.queryParams || []);
        }
    }

    async updateMyEventsListAfterDependencyUpdate(): Promise<void> {
        if (this.myEventsData.companyManagerDependencyId) {
            const companyManagerDependencyResult = this.applyDependenciesResult.dependencyResults.find(x => x.dependency.id === this.myEventsData.companyManagerDependencyId);
            if (companyManagerDependencyResult) {
                this.myEventsData.companyManagerDependencyResult = companyManagerDependencyResult.result;
            } else {
                this.myEventsData.companyManagerDependencyResult = true;
            }
        } else {
            this.myEventsData.companyManagerDependencyResult = true;
        }
        if (this.myEventsData.lastResponse?.events) {
            const eventTokenInfo = this.getCurrentEventTokenInfo();
            this.myEventsData.rowActionGetter = (rowItem: IProfileEvent) => {
                const stageActionRowActions = this.myEventsSvc.createParticipantStageActionRowActions(rowItem, () => this.myEventsTableSettingsChanged(this.myEventsData.lastTableSettings));
                const actionObject: IRowActionObject<IProfileEvent> = {
                    otherActions: stageActionRowActions
                };

                const isViewLinkVisible = rowItem.can_impersonate && this.myEventsSvc.isViewLinkVisible(
                    rowItem,
                    eventTokenInfo.payload.participant_id,
                    eventTokenInfo.payload.contact_id,
                    eventTokenInfo.payload.om,
                    this.myEventsData.companyManagerDependencyResult
                );
                if (isViewLinkVisible) {
                    actionObject.viewFn = (row) => this.onMyEventsRowActionSelected(row);
                }
                return actionObject;
            };
        }
    }

    updateFromMultiSelectFields(eventParticipantFields: IField[], eventParticipant: IObjectWithId): void {
        // Adjust the values of fields of type `select` if they refer items from other field
        const fromMultiSelectFields = eventParticipantFields.filter(x => x.type === 'select');
        for (const fromMultiSelectField of fromMultiSelectFields) {
            fromMultiSelectField.type = 'select';
            const referencedFieldId = fromMultiSelectField.options?.items_from_field_id;
            if (referencedFieldId) {
                const referencedParticipantField = eventParticipantFields.find(x => x.id === referencedFieldId || x.crm_field_id === referencedFieldId);
                if (referencedParticipantField) {
                    const participantFieldValue = eventParticipant[referencedParticipantField.id];
                    if (participantFieldValue && Array.isArray(participantFieldValue)) {
                        const arr = participantFieldValue as any[];
                        fromMultiSelectField.schema.enum = ['', ...arr];
                        fromMultiSelectField.attributeTranslations = referencedParticipantField.attributeTranslations;
                    }
                }
            }
        }
    }

    private processSpecialPage(): void {
        // Detect if this is a special type of page

        // Detect if this is login page
        const isLoginPageResult = this.isLoginPage();
        if (isLoginPageResult.isLogIn) {
            this.processLoginPage(isLoginPageResult);
            return;
        }

        const isResetPasswordPageResult = this.isResetPasswordPage();
        if (isResetPasswordPageResult.isResetPassword) {
            this.processResetPasswordPage(isResetPasswordPageResult);
            return;
        }

        this.profileSubscriptionService.loadSubscriptionsItemElements();
    }

    async processResetPasswordPage(pageResult: IIsResetPasswordPageResult): Promise<void> {
        pageResult.submitButtonEl.onclick = async ev => {
            if (pageResult.passwordEl.value.length === 0 || pageResult.passwordEl.value !== pageResult.repeatPasswordEl.value) {
                await this.modalDialogSvc.showMessageBox(this.translateSvc.instant(TranslationKey.passwordsDoesNotMatch));
            } else {
                await this.apiSvc.changeProfilePassword(pageResult.passwordEl.value);
                await this.modalDialogSvc.showMessageBox(this.translateSvc.instant(TranslationKey.passwordHasBeenChanged));
                this.loadEventHomePage();
            }
        };
    }

    private setLoginPageSectionsVisibility(pageResult: IIsLoginPageResult): void {
        // Hide sections according to event settings
        const access = this.eventSettings.login;
        if (pageResult.loginFormContainer) {
            const showLogIn = access.includes(LoginMethod.azavista);
            this.domSvc.changeElementVisibilityWithCss(pageResult.loginFormContainer, showLogIn);
            if (pageResult.loginButtonSectionEl) {
                this.domSvc.changeElementVisibilityWithCss(pageResult.loginButtonSectionEl, showLogIn);
            }
            if (pageResult.passwordResetButtonSectionEl) {
                this.domSvc.changeElementVisibilityWithCss(pageResult.passwordResetButtonSectionEl, showLogIn);
            }
        }
        if (pageResult.createAccountButtonSectionEl) {
            const showCreateAccount = access.includes(LoginMethod.createaccount);
            this.domSvc.changeElementVisibilityWithCss(pageResult.createAccountButtonSectionEl, showCreateAccount);
        }
        if (pageResult.loginLinkSectionEl && pageResult.loginLinkButtonEl) {
            const showLoginLink = access.includes(LoginMethod.loginlink);
            this.domSvc.changeElementVisibilityWithCss(pageResult.loginLinkSectionEl, showLoginLink);
        }
        if (pageResult.accessCodeContainerEl) {
            const showAccessCode = access.includes(LoginMethod.accesscode);
            this.domSvc.changeElementVisibilityWithCss(pageResult.accessCodeContainerEl, showAccessCode);
            if (pageResult.accessCodeButtonContainerEl) {
                this.domSvc.changeElementVisibilityWithCss(pageResult.accessCodeButtonContainerEl, showAccessCode);
            }
        }
    }

    processLoginPage(pageResult: IIsLoginPageResult): void {
        this.setLoginPageSectionsVisibility(pageResult);

        if (pageResult.loginButtonEl) {
            pageResult.loginButtonEl.onclick = ev => this.processLoginPageLoginClick(pageResult, ev);
        }
        if (pageResult.passwordResetButtonEl) {
            pageResult.passwordResetButtonEl.onclick = ev => this.processLoginPageResetPasswordClick(pageResult, ev);
        }
        if (pageResult.createAccountButtonEl) {
            pageResult.createAccountButtonEl.onclick = ev => this.processLoginPageCreateAccountClick(pageResult, ev);
        }
        if (pageResult.accessCodeButtonEl) {
            pageResult.accessCodeButtonEl.onclick = ev => this.processLoginPageAccessCodeButtonClick(pageResult, ev);
        }
        if (pageResult.loginLinkButtonEl) {
            pageResult.loginLinkButtonEl.onclick = ev => this.processLoginPageLoginLinkButtonClick(pageResult, ev);
        }
    }

    private async processLoginPageAccessCodeButtonClick(isLoginPageResult: IIsLoginPageResult, ev: Event): Promise<void> {
        ev.preventDefault();
        ev.stopPropagation();

        const accessCode = isLoginPageResult.accessCodeInputEl?.value?.trim();
        if (!accessCode) {
            this.notificationSvc.showNotification(NotificationMessageType.warning, this.appSvc.translate(TranslationKey.accessCodeNotProvided));
            return;
        }

        try {
            const res = await this.apiSvc.accessCodeLogin(this.eventId, accessCode);
            this.loadEventPage(this.pageData.id, false);
        } catch (err) {
            this.appLoggerSvc.log(LogLevel.error, err);
            this.notificationSvc.showNotification(NotificationMessageType.error, this.appSvc.translate(TranslationKey.incorrectAccessCode));
        }
    }

    async processLoginPageCreateAccountClick(isLoginPageResult: IIsLoginPageResult, ev: Event): Promise<void> {
        ev.preventDefault();
        ev.stopPropagation();
        // Create account is the same as reset password - requires email to be provided - just use different dialog title
        const config: IEnterEmailDialogConfig = {
            title: this.translateSvc.instant(TranslationKey.createAccount)
        };
        const dlgResult = await this.enterEmailDialogSvc.showPopupDialog(config);
        this.enterEmailDialogSvc.hidePopupDialog();
        if (!(dlgResult && dlgResult.email?.trim())) {
            return;
        }
        await this.apiSvc.sendCreateAccountLink(dlgResult.email.trim());
        this.enterEmailDialogSvc.hidePopupDialog();
        this.modalDialogSvc.showMessageBox(this.translateSvc.instant(TranslationKey.checkYourEmailInboxForCreateAccountLink));
    }

    async processLoginPageResetPasswordClick(isLoginPageResult: IIsLoginPageResult, ev: Event): Promise<void> {
        ev.preventDefault();
        ev.stopPropagation();
        const config: IEnterEmailDialogConfig = {
            title: this.translateSvc.instant(TranslationKey.resetPassword)
        };
        const dlgResult = await this.enterEmailDialogSvc.showPopupDialog(config);
        this.enterEmailDialogSvc.hidePopupDialog();
        if (!(dlgResult && dlgResult.email?.trim())) {
            return;
        }
        await this.apiSvc.sendPasswordResetLink(dlgResult.email.trim());
        this.enterEmailDialogSvc.hidePopupDialog();
        this.modalDialogSvc.showMessageBox(this.translateSvc.instant(TranslationKey.checkYourEmailInboxForPasswordResetLink));
    }

    async processLoginPageLoginLinkButtonClick(isLoginPageResult: IIsLoginPageResult, ev: Event): Promise<void> {
        ev.preventDefault();
        ev.stopPropagation();
        const config: IEnterEmailDialogConfig = {
            title: this.translateSvc.instant(TranslationKey.loginLink),
        };
        const dlgResult = await this.enterEmailDialogSvc.showPopupDialog(config);
        this.enterEmailDialogSvc.hidePopupDialog();
        if (!(dlgResult && dlgResult.email?.trim())) {
            return;
        }
        await this.apiSvc.sendLoginLink(dlgResult.email.trim());
        this.enterEmailDialogSvc.hidePopupDialog();
        this.modalDialogSvc.showMessageBox(this.translateSvc.instant(TranslationKey.isThisEmailHasAnAccountAnEmailWithALoginLinkHasBeenSent));
    }

    private async processLoginPageLoginClick(isLoginPageResult: IIsLoginPageResult, ev: MouseEvent): Promise<void> {
        ev.preventDefault();
        ev.stopPropagation();
        const username = isLoginPageResult.usernameEl.value?.trim();
        if (!username) {
            return;
        }
        const password = isLoginPageResult.passwordEl.value;
        if (!password) {
            return;
        }
        await this.profileLogin(username, password);
        await this.tokenGetEventTokenUsingCurrentIdentityToken();
        this.loadEventPage(this.pageData.id, false);
    }

    private async profileLogin(username: string, password: string): Promise<void> {
        await this.apiSvc.profileLogin(username, password, this.eventId);
        await this.loadAndStoreProfilePage();
    }

    private isResetPasswordPage(): IIsResetPasswordPageResult {
        const result: IIsResetPasswordPageResult = {
            isResetPassword: false
        };
        const resetPasswordFieldsContainer = this.domSvc.getDynamicContainerChildElement(
            this.domSvc.getAttributeSelector(AttributeName.azaFormPasswordReset)
        );
        if (resetPasswordFieldsContainer) {
            const passwordEl = resetPasswordFieldsContainer.querySelector('#password');
            if (passwordEl) {
                const repeatPasswordEl = resetPasswordFieldsContainer.querySelector('#repeat-password');
                if (repeatPasswordEl) {
                    const submitButtonEl = this.domSvc.getDynamicContainerChildElement(
                        this.domSvc.getAttributeSelector(AttributeName.azaPasswordUpdateButton)
                    );
                    if (submitButtonEl) {
                        result.isResetPassword = true;
                        result.passwordEl = passwordEl as HTMLInputElement;
                        result.repeatPasswordEl = repeatPasswordEl as HTMLInputElement;
                        result.submitButtonEl = submitButtonEl as HTMLButtonElement;
                        return result;
                    }
                }
            }
        }

        result.isResetPassword = false;
        return result;
    }

    private isLoginPage(): IIsLoginPageResult {
        const result: IIsLoginPageResult = {
            isLogIn: false
        };
        const accessCodeContainerEl = this.domSvc.getDynamicContainerChildElement(this.domSvc.getAttributeSelector(AttributeName.azaFormAccessCode));
        if (accessCodeContainerEl) {
            const accessCodeInputEl: HTMLInputElement = accessCodeContainerEl.querySelector('#' + ElementId.accessCode);
            if (accessCodeInputEl) {
                const accessCodeButtonSectionEl = this.domSvc.getDynamicContainerChildElement(this.domSvc.getAttributeSelector(AttributeName.azaFormLoginAccessCodeButtonSection));
                if (accessCodeButtonSectionEl) {
                    const accessCodeButtonEl: HTMLButtonElement = accessCodeButtonSectionEl.querySelector(this.domSvc.getAttributeSelector(AttributeName.azaAccessCodeButton));
                    if (accessCodeButtonEl) {
                        result.isLogIn = true;
                        result.accessCodeContainerEl = accessCodeContainerEl;
                        result.accessCodeButtonContainerEl = accessCodeButtonSectionEl;
                        result.accessCodeInputEl = accessCodeInputEl;
                        result.accessCodeButtonEl = accessCodeButtonEl;
                    }
                }
            }
        }

        const loginFormContainer = this.domSvc.getDynamicContainerChildElement(this.domSvc.getAttributeSelector(AttributeName.azaFormLogin));
        if (loginFormContainer) {
            const usernameEl = loginFormContainer.querySelector('#username');
            if (usernameEl) {
                const passwordEl = loginFormContainer.querySelector('#password');
                if (passwordEl) {
                    const loginButtonEl = this.domSvc.getDynamicContainerChildElement(
                        `button${this.domSvc.getAttributeSelector(AttributeName.azaLoginButton)}`
                    );
                    if (loginButtonEl) {
                        result.isLogIn = true;
                        result.loginFormContainer = loginFormContainer;
                        result.loginButtonSectionEl = this.domSvc.getDynamicContainerChildElement(
                            this.domSvc.getAttributeSelector(AttributeName.azaFormLoginLoginButtonSection)
                        );
                        result.usernameEl = usernameEl as HTMLInputElement;
                        result.passwordEl = passwordEl as HTMLInputElement;
                        result.loginButtonEl = loginButtonEl as HTMLButtonElement;
                        const passwordResetButtonEl = this.domSvc.getDynamicContainerChildElement(
                            `button${this.domSvc.getAttributeSelector(AttributeName.azaPasswordResetButton)}`
                        );
                        result.passwordResetButtonEl = passwordResetButtonEl as HTMLButtonElement;
                        const createAccountButtonEl = this.domSvc.getDynamicContainerChildElement(
                            `button${this.domSvc.getAttributeSelector(AttributeName.azaCreateAccountButton)}`
                        );
                        result.createAccountButtonEl = createAccountButtonEl as HTMLButtonElement;
                        result.createAccountButtonSectionEl = this.domSvc.getDynamicContainerChildElement(
                            this.domSvc.getAttributeSelector(AttributeName.azaFormLoginCreateAccountButtonSection)
                        );
                        result.passwordResetButtonSectionEl = this.domSvc.getDynamicContainerChildElement(
                            this.domSvc.getAttributeSelector(AttributeName.azaFormLoginPasswordResetButtonSection)
                        );
                        result.loginLinkSectionEl = this.domSvc.getDynamicContainerChildElement(
                            this.domSvc.getAttributeSelector(AttributeName.azaFormLoginLinkButtonSection)
                        );
                        if (result.loginLinkSectionEl) {
                            result.loginLinkButtonEl = this.domSvc.getChildElementBySelector(
                                result.loginLinkSectionEl,
                                `button${this.domSvc.getAttributeSelector(AttributeName.azaLoginLinkButton)}`
                            );
                        }
                    }
                }
            }
        }

        return result;
    }

    private showProfilePage(profilePageHashId: string): void {
        const menuItemsMap: IIdWithValue[] = [
            { id: 'data-profile', value: SideMenuItemId.myProfile },
            { id: 'data-profile-events', value: SideMenuItemId.events },
            { id: 'data-profile-requests', value: SideMenuItemId.requests },
            { id: 'data-profile-orders', value: SideMenuItemId.myOrders },
            { id: 'data-profile-organization', value: SideMenuItemId.myOrganization },
            { id: 'data-profile-password', value: SideMenuItemId.changePassword },
            { id: 'data-profile-subscriptions', value: SideMenuItemId.subscriptions }
        ];
        const menuItemMap = menuItemsMap.find(x => x.id === profilePageHashId);
        if (menuItemMap) {
            const sideMenuItem = this.sideMenuItems.find(x => x.id === menuItemMap.value);
            if (sideMenuItem) {
                this.sideMenuItemClicked(sideMenuItem);
            }
        }
    }

    async showCancellationDialog(orders: IOrder[]): Promise<string> {
        const msgLines = this.getCancellationDialogMessageLines(orders);
        const doYouWantToCancelTranslated = this.appSvc.translate(TranslationKey.doYouWantToCancel);
        const dialogConfig: IModalDialogConfig = {
            title: doYouWantToCancelTranslated,
            textLines: msgLines,
            firstButton: { id: 'back', text: this.appSvc.translate(TranslationKey.cancelback) },
            secondButton: { id: 'ok', text: this.appSvc.translate(TranslationKey.cancelok) }
        };
        const dlgResult = await this.modalDialogSvc.showPopupDialog(dialogConfig);
        this.modalDialogSvc.hidePopupDialog();
        return dlgResult.clickedButtonId;
    }

    private getCancellationDialogMessageLines(orders: IOrder[]): string[] {
        const msgLines: string[] = [];
        const ordersWithTransactions = orders.filter(x => x.transactions);
        let totalAmount = 0;
        let currency = '';
        let refund = 0;
        if (this.hasShoppingCartV2) {
            // We need to call the library with all products quantities set to 0 to get the cancellation calculated
            this.appLoggerSvc.group('@azavista/payments');
            this.appLoggerSvc.log(
                LogLevel.log,
                'Shopping cart before setting all products quantities to 0 to calculate cancellation cost',
                this.shoppingCartV2Data
            );
            this.appLoggerSvc.groupEnd();
            const shoppingCartV2CartItemsCloned = this.sharedSvc.clone(this.shoppingCartV2Cmp.cartItems);
            // Simulate that the user has set all the quantities to 0 to calculate the cancellation cost
            shoppingCartV2CartItemsCloned.forEach(x => x.selectedQuantity = 0);
            const shoppingCartV2ResultUI = this.getShoppingCartResultUI();
            this.appLoggerSvc.group('@azavista/payments');
            this.appLoggerSvc.log(
                LogLevel.log,
                'Shopping cart result after calculating cancellation cost',
                this.shoppingCartV2Data,
                shoppingCartV2ResultUI
            );
            this.appLoggerSvc.groupEnd();
            // https://az2019.azavista.com/product-management/frontend/azavista3.0/-/issues/4476
            totalAmount = shoppingCartV2ResultUI.totals.total - shoppingCartV2ResultUI.totals.current_order_total;
            refund = -shoppingCartV2ResultUI.totals.current_order_total;
            // We will try to get the currency from the first product of the existing order
            currency = ordersWithTransactions[0]?.transactions?.[0].items[0]?.product.currency;
        }
        const cancellationPolicy = this.appSvc.translate(TranslationKey.cancellationPolicy);
        msgLines.push(`${cancellationPolicy}:`);
        const orderAmountTranslated = this.appSvc.translate(TranslationKey.orderAmount);
        msgLines.push(`${orderAmountTranslated} ${totalAmount} ${currency}`);
        const refundTranslated = this.appSvc.translate(TranslationKey.refundAmountIs);
        msgLines.push(`${refundTranslated} ${refund} ${currency}`);
        msgLines.push('');

        return msgLines;
    }

    async processCancellationButtonClicked(): Promise<void> {
        // Check the token - if there is no participant_id then show popup "You are not registered. Cannot cancel"
        const participantId = this.getCurrentEventTokenInfo()?.payload?.participant_id;
        if (!participantId) {
            const dialogConfig: IModalDialogConfig = {
                title: '',
                textLines: [this.appSvc.translate(TranslationKey.youAreNotRegisteredCannotCancel)],
                firstButton: { id: 'ok', text: this.appSvc.translate(TranslationKey.ok) }
            };
            await this.modalDialogSvc.showPopupDialog(dialogConfig);
            this.modalDialogSvc.hidePopupDialog();
            return;
        }

        let paymentCancellationDialogShown = false;

        try {
            const orders = await this.apiSvc.getPaymentOrders();
            if (orders.length > 0) {
                paymentCancellationDialogShown = true;
                const answer = await this.showCancellationDialog(orders);
                if (answer !== 'ok') {
                    return;
                }
            }
        } catch (err) {
        }

        // If we didn't show popup from payments - show cancellation confirmation dialog
        if (!paymentCancellationDialogShown) {
            const doYouWantToCancelTranslated = this.appSvc.translate(TranslationKey.doYouWantToCancel);
            const dialogConfig: IModalDialogConfig = {
                title: '',
                textLines: [doYouWantToCancelTranslated],
                firstButton: { id: 'back', text: this.appSvc.translate(TranslationKey.cancelback) },
                secondButton: { id: 'ok', text: this.appSvc.translate(TranslationKey.cancelok) }
            };
            const dialogResult = await this.modalDialogSvc.showPopupDialog(dialogConfig);
            this.modalDialogSvc.hidePopupDialog();
            if (dialogResult.clickedButtonId !== 'ok') {
                return;
            }
        }
        const res = await this.apiSvc.cancelProcess();
        if (res.next_page_id) {
            this.loadEventPage(res.next_page_id);
        } else {
            this.loadEventHomePage();
        }
    }

    validateAllFields(fields: IField[]): void {
        const fieldsWithElements: IFieldWithInputElement[] = [];
        for (const field of fields) {
            const els = this.domSvc.getAllInputElementsByFieldId(field.id);
            els.forEach(el => {
                fieldsWithElements.push({ field: field, element: el });
            });
            const documentEls = this.domSvc.getAllDocumentElementsByFieldId(field.id);
            documentEls.forEach(el => {
                fieldsWithElements.push({ field: field, element: el });
            });
            const imageEls = this.domSvc.getAllImageElementsByFieldId(field.id);
            imageEls.forEach(el => {
                fieldsWithElements.push({ field: field, element: el });
            });
        }
        this.validatorSvc.validateAllFields(fieldsWithElements);
    }

    getShoppingCartDataFromV2(data: IShoppingCartV2ChangedData): IShoppingCartChangedData {
        const { selectedPayByOption, discount, discountCode, visibleProductsWithQuantity, visibleTicketProducts } = data ?? {};
        return {
            productsWithQuantity: [
                ...visibleProductsWithQuantity,
                ...visibleTicketProducts
            ],
            discount,
            discountCode,
            selectedPayByOption,
            currentOrderTotal: data.currentOrderTotal,
            cartCurrency: data.cartCurrency
        };
    }

    async onShoppingCartChanged(data: IShoppingCartChangedData): Promise<void> {
        this.lastShoppingCartChangedData = { ...data };

        this.cartItems = data.productsWithQuantity.map(({ quantity, product }) => ({
            product,
            cart_quantity: quantity,
        }));

        this.updateMultiRegistrationValue();
        this.multiRegistrationSvc.controller?.setCartData(this.getShoppingCartResultUI());
        await this.reloadDependencies();
    }

    private updateMultiRegistrationValue() {
        if (this.hasShoppingCartV2) {
            const shoppingCartV2SectionEl = this.domSvc.getDynamicContainerChildElement(this.domSvc.getAttributeSelector(AttributeName.azaShoppingCartV2Section));
            if (shoppingCartV2SectionEl.id === 'multireg') {
                this.domSvc.setTicketMultiRegistrationFields(
                    this.eventParticipantFields,
                    this.cartItems,
                    this.shoppingCartProductsVisibility,
                );
            }
        }
    }

    async onCropperImageCropped(ev: ImageCroppedEvent) {
        this.croppedImageBase64 = await this.getResizedImage(ev.base64, {
            width: this.cropperMaxWidth,
            height: this.cropperMaxHeight,
        });
        const base64Marker = 'base64,';
        const base64Image = this.croppedImageBase64.substr(this.croppedImageBase64.indexOf(base64Marker) + base64Marker.length);
        const binaryImg = window.atob(base64Image);
        const length = binaryImg.length;
        const arrayBuffer = new ArrayBuffer(length);
        const uintArray = new Uint8Array(arrayBuffer);
        for (let i = 0; i < length; i++) {
            uintArray[i] = binaryImg.charCodeAt(i);
        }
        this.croppedImageBytes = uintArray;
    }

    onCropperImageLoaded(): void {

    }

    onCropperReady(data: any): void {

    }

    onCropperLoadImageFailed(): void {

    }

    async uploadAllImages(): Promise<IUploadDocumentResult[]> {
        const promise = new Promise<IUploadDocumentResult[]>(async (resolve, reject) => {
            const keys = Object.keys(this.croppedImagesMap);
            if (keys.length === 0) {
                return resolve([]);
            }
            const uploadDocResults: IUploadDocumentResult[] = [];
            for (const key of keys) {
                const croppedImageData = this.croppedImagesMap[key];
                const imageBytesArray = croppedImageData.buffer;
                let fileName = (croppedImageData?.file?.name || this.eventParticipantFields.find(x => x.id === key).label).trim();
                if (!fileName.endsWith('.png')) {
                    fileName = `${fileName}.png`;
                }
                const existingDocumentId = this.eventParticipant && this.eventParticipant[key];
                let asset: IAsset;
                const uploadNewDocument = () => this.apiSvc.uploadParticipantDocument(fileName, 'image/png', imageBytesArray);
                if (existingDocumentId) {
                    // Update existing
                    asset = await this.apiSvc.updateDocument(existingDocumentId, fileName, 'image/png', imageBytesArray).catch(err => {
                        if (err?.communicationError?.body?.code === API_RESPONSE_CODE_DOCUMENT_NOT_FOUND) {
                            return uploadNewDocument();
                        }
                    });
                } else {
                    // Create new
                    asset = await uploadNewDocument();
                }
                uploadDocResults.push({ fieldId: key, uploadedAsset: asset });
            }
            return resolve(uploadDocResults);
        });
        return promise;
    }


    async uploadDocument(callbackData: IDocumentFieldCallbackData): Promise<IUploadDocumentResult> {
        return new Promise((resolve, reject) => {
            const file = (callbackData.event.target as HTMLInputElement).files[0];
            const existingDocumentId = this.eventParticipant && this.eventParticipant[callbackData.fieldId];
            let obs: Observable<HttpEvent<IAsset>>;
            const uploadNewEntityDocument$ = this.apiSvc.uploadParticipantDocumentFile(file);
            if (existingDocumentId) {
                // Update existing
                obs = this.apiSvc.updateDocumentFile(existingDocumentId, file).pipe(
                    catchError(err => {
                        // if the related documentId is not found then uploadNewEntityDocument
                        return err?.error?.code === API_RESPONSE_CODE_DOCUMENT_NOT_FOUND ? uploadNewEntityDocument$ : throwError(() => err);
                    })
                );
            } else {
                // Create new
                obs = uploadNewEntityDocument$;
            }
            obs.pipe(
                map(ev => {
                    if (ev.type === HttpEventType.UploadProgress) {
                        // this.appLoggerSvc.log('log', ev.loaded, ev.total);
                        const percentage = Math.round(100 * (ev.loaded / ev.total));
                        this.domSvc.showDocumentUploadProgress(callbackData.fieldId, percentage);
                    } else if (ev.type === HttpEventType.Response) {
                        return ev.body;
                    }
                }),
                tap(msg => { }),
                last()
            ).subscribe({
                next: data => resolve({ fieldId: callbackData.fieldId, uploadedAsset: data } as IUploadDocumentResult),
                error: err => reject(err)
            });
        });
    }

    async uploadAllDocuments(): Promise<IUploadDocumentResult[]> {
        const promise = new Promise<IUploadDocumentResult[]>(async (resolve, reject) => {
            const keys = Object.keys(this.documentFieldsFilesMap);
            if (keys.length === 0) {
                return resolve([]);
            }
            const obsArray: IUploadDocumentResult[] = [];
            for (const key of keys) {
                const callbackData = this.documentFieldsFilesMap[key];

                if (callbackData.clickedItem === DocumentFieldCallbackDataClickedItemName.inputField
                    && (callbackData.event.target as HTMLInputElement).files.length > 0) {
                    try {
                        const res = await this.uploadDocument(callbackData);
                        obsArray.push(res);
                    } catch (err) {
                        return reject(err);
                    }
                }
            }
            resolve(obsArray);
        });
        return promise;
    }

    getEventParticipantUtmCodes(): IIdWithValue[] {
        const foundCodes: IIdWithValue[] = [];
        const utmCodesFromPageData = this.pageData?.utmcode || [];
        const isQueryParamBuiltinField = (idWithValue: IIdWithValue, field: IField) =>
            idWithValue.id === field.name && !!field.builtin;
        const isQueryParamCrmField = (idWithValue: IIdWithValue, field: IField) =>
            idWithValue.id + '__crm' === field.name;
        for (const utmCode of utmCodesFromPageData) {
            const field = this.eventParticipantFields.find(x => x.crm_field_id === utmCode);
            if (field) {
                // Participant field with the specified crm_field_id OR builtin-field is found
                // Try to find whether we have utm code from url for this field
                const utmCodeFromUrl = this.queryParams.find(idWithValue => {
                    return isQueryParamCrmField(idWithValue, field)
                        || isQueryParamBuiltinField(idWithValue, field);
                });
                if (utmCodeFromUrl) {
                    // We have found utm code from url for this field - add the field id and url parameter value to the result
                    foundCodes.push({ id: field.id, value: utmCodeFromUrl.value });
                }
            }
        }
        return foundCodes;
    }

    async onActivitiesCartChanged(activityCartItems: ICartActivityItem[]): Promise<void> {
        this.activityCartItems = activityCartItems;
        await this.reloadDependencies();
    }

    private async sendRegisteredActivities(): Promise<void> {
        // Send activities to which the participant has registered
        const registeredActivitiesIds = this.activityCartItems.filter(x => x.selected).map(x => x.cartActivity.id);
        try {
            const req: IRegisterToActivitiesRequest = { activity_ids: registeredActivitiesIds };
            await this.apiSvc.registerToActivities(req);
        } catch { }
    }

    async submitForm(): Promise<void> {
        if (this.hasFormErrors(true)) {
            return;
        }

        // Check if activities cart is required but no activities selected
        if (this.isActivitiesCartRequired) {
            const registeredActivitiesIds = this.activityCartItems.filter(x => x.selected).map(x => x.cartActivity.id);
            if (registeredActivitiesIds.length === 0) {
                await this.modalDialogSvc.showPopupDialog({
                    firstButton: { id: null, text: this.translateSvc.instant(TranslationKey.ok) },
                    textLines: [this.translateSvc.instant(TranslationKey.itIsMandatoryToSelectAtLeastOneActivityOrSession)]
                });
                this.modalDialogSvc.hidePopupDialog();
                return;
            }
        }

        // First validate products if any
        let shouldStartPaymentV2 = false;
        let productIdsWithQuantity: IProductIdWithQuantity[];
        let shouldShowPaymentTypeNotSelectedPopUp = false;

        if (this.hasShoppingCartV2) {
            // Check if the cart has dependency and if yes - whether it is fulfilled
            if (this.shoppingCartV2Data.dependency && this.applyDependenciesResult?.dependencyResults) {
                const shoppingCartV2DependencyResult = this.applyDependenciesResult.dependencyResults.find(x => x.dependency.id === this.shoppingCartV2Data.dependency);
                if (shoppingCartV2DependencyResult) {
                    shouldStartPaymentV2 = shoppingCartV2DependencyResult.result;
                }
            } else {
                shouldStartPaymentV2 = true;
            }
        }
        const shoppingCartV2ChangedData = this.shoppingCartV2Cmp.getChangedData();

        if (shouldStartPaymentV2) {
            productIdsWithQuantity = this.createProductIdWithQuantities(
                shoppingCartV2ChangedData.visibleProductsWithQuantity
            );
            // TODO: Item validation is just a boolean - we don't know what exactly is not valid
            //       For now it is just that there are ticket products but none is selected for purchase
            if (!shoppingCartV2ChangedData.areItemsValid) {
                const visibleTicketProductIdWithQuantities = this.createProductIdWithQuantities(shoppingCartV2ChangedData.visibleTicketProducts || []);
                await this.showTicketProductsNotSelectedPopUp(visibleTicketProductIdWithQuantities);
                return;
            }

            // Check if payment type must be selected but is not
            if (this.productResponse?.pay_by_invoice && !shoppingCartV2ChangedData.selectedPayByOption) {
                shouldShowPaymentTypeNotSelectedPopUp = true;
            }
        }

        if (shouldShowPaymentTypeNotSelectedPopUp) {
            const dialogConfig: IModalDialogConfig = {
                firstButton: { id: 'ok', text: 'OK' },
                title: this.translateSvc.instant(TranslationKey.paymentTypeIsNotSelected),
                textLines: [this.translateSvc.instant(TranslationKey.selectPaymentType)]
            };
            await this.modalDialogSvc.showPopupDialog(dialogConfig);
            this.modalDialogSvc.hidePopupDialog();
            return;
        }

        // TODO: According to menu/page type (for now only 'aza-form') - select the right fields set and endpoint to post to
        let htmlFormData = this.domSvc.createJsonFromFormFields(
            this.eventParticipantFields, this.hiddenFieldIds, this.eventSettings.timezone
        );
        if (this.eventParticipant) {
            Object.keys(htmlFormData).forEach(key => {
                if (!this.eventParticipant[key]) {
                    if (htmlFormData[key] === '') {
                        delete htmlFormData[key];
                    }
                    if (Array.isArray(htmlFormData[key]) &&
                        htmlFormData[key].length === 0) {
                        delete htmlFormData[key];
                    }
                } else {
                    // TODO: this.eventTokenResponse is not set when the user is not yet registered and logs in from the profile link
                    if (this.eventTokenResponse?.token?.participant_id) {
                        if (htmlFormData[key] === this.eventParticipant[key]) {
                            delete htmlFormData[key];
                        }
                    }
                }
            });

            const deletedImageFieldIds = Object.keys(this.deletedImageFieldValuesMap);
            const deletedDocumentFieldIds = Object.keys(this.deletedDocumentFieldsFilesMap);
            const deletedImageAndDocumentFieldIds = [...deletedImageFieldIds, ...deletedDocumentFieldIds];
            for (const fieldId of deletedImageAndDocumentFieldIds) {
                htmlFormData[fieldId] = null;
            }
        }

        // Add utm codes if matches are found
        const utmCodes = this.getEventParticipantUtmCodes();
        if (utmCodes.length > 0) {
            // We have match(es) between utm codes from url and participant fields - set them to the payload
            for (const utmCode of utmCodes) {
                htmlFormData[utmCode.id] = utmCode.value;
            }
        }

        // Merge utm object properties
        htmlFormData = this.appSvc.mergeHtmlFormDataWithUtmObject(htmlFormData, this.utmParamValue, this.mergeParticipantWithUtmObjectResult);

        const preferredLanguageField = this.appSvc.getPreferredLanguageField(this.eventParticipantFields);
        if (preferredLanguageField && !htmlFormData[preferredLanguageField.id]) {
            htmlFormData[preferredLanguageField.id] = this.currentLanguage;
        }
        if (this.eventParticipant && this.eventParticipant.id) {
            // We already have participant - update it
            try {
                const updateEventParticipantResponse = await this.apiSvc.updateEventParticipant(htmlFormData, FieldMode.id);
                // The server no longer returns the participant
                // this.eventParticipant = updateEventParticipantResponse.participant;
            } catch (err) {
                this.notificationSvc.showNotification(
                    NotificationMessageType.error, this.appSvc.translate(TranslationKey.cannotUpdateTheParticipant)
                );
                return;
            }
        } else {
            // We do not have participant - create it
            try {
                const createEventParticipantResponse = await this.apiSvc.createEventParticipant(htmlFormData, FieldMode.id);
                // The response from the server no longer returns the participant in its own "participant" property
                // The participant properties are returned as part of the response object
                // The response can contain "token" - it is not part of the participant and we must remove it
                delete createEventParticipantResponse.token;
                this.eventParticipant = { ...this.eventParticipant, ...createEventParticipantResponse };
            } catch (err) {
                if (this.appSvc.isApiErrorEmailConflict({ error: err } as IErrorNotification)) {
                    // Start flow for refreshing the token
                    const dialogResult = await this.emailCodeDialogSvc.showPopupDialog({});
                    const emailCode = dialogResult?.emailCode?.trim();
                    this.emailCodeDialogSvc.hidePopupDialog();
                    if (!emailCode) {
                        return;
                    }
                    const emailField = this.eventParticipantFields.find(x => x.name === 'email');
                    try {
                        const getIdentityTokenResponse = await this.apiSvc.getIdentityToken({
                            registrationCode: emailCode,
                            email: htmlFormData[emailField.id]
                        }, true);
                        await this.tokenGetEventToken();
                        // After obtaining new tokens reload current page
                        await this.loadEventPage(this.pageData.id);
                        return;
                    } catch (renewTokenErr) {
                        this.notificationSvc.showNotification(
                            NotificationMessageType.error, this.appSvc.translate(TranslationKey.cannotGenerateNewTokens)
                        );
                        return;
                    }
                } else {
                    this.notificationSvc.showNotification(
                        NotificationMessageType.error, this.appSvc.translate(TranslationKey.cannotCreateTheParticipant)
                    );
                    return;
                }
            }
        }

        if (this.hasActivitiesCart) {
            // There is activity cart in the page, check if it is hidden because of dependency
            let shouldRegisterActivities = true;
            const activitiesCartDependencyId = this.domSvc.getActivitiesCartElementDependencyId();
            if (activitiesCartDependencyId) {
                const activitiesCartDependencyResult = this.applyDependenciesResult.dependencyResults.find(x => x.dependency.id === activitiesCartDependencyId);
                if (activitiesCartDependencyResult?.result === false) {
                    // Activities cart is hidden by dependency
                    shouldRegisterActivities = false;
                }
            }
            if (shouldRegisterActivities) {
                await this.sendRegisteredActivities();
            }
        }

        // Prepare object that will collect ids of uploaded images and documents
        const docAndImageFieldsObj = {} as any;

        // Upload all cropped images
        try {
            const uploadImagesResult = await this.uploadAllImages();
            if (uploadImagesResult.length > 0) {
                for (const imgResult of uploadImagesResult) {
                    docAndImageFieldsObj[imgResult.fieldId] = imgResult.uploadedAsset.id;
                }
            }
        } catch (err) {
            this.notificationSvc.showNotification(NotificationMessageType.error, this.appSvc.translate(TranslationKey.errorOccuredOnImageUpload));
            return;
        }

        // Upload all document fields
        try {
            const uploadDocumentsResult = await this.uploadAllDocuments();
            if (uploadDocumentsResult.length > 0) {
                // Update participant fields for all uploaded files
                for (const docResult of uploadDocumentsResult) {
                    docAndImageFieldsObj[docResult.fieldId] = docResult.uploadedAsset.id;
                }
            }
        } catch (err) {
            // TODO: Signal that uploading documents dir not succeeded
            const msg = `this.appSvc.translate(TranslationKey.errorOccuredOnFileUpload) : ${err?.code}`;
            this.notificationSvc.showNotification(NotificationMessageType.error, this.appSvc.translate(TranslationKey.errorOccuredOnFileUpload));
            return;
        }

        if (Object.keys(docAndImageFieldsObj).length > 0) {
            try {
                await this.apiSvc.updateEventParticipant(docAndImageFieldsObj, FieldMode.id);
            } catch (err) {
                this.notificationSvc.showNotification(
                    NotificationMessageType.error, this.appSvc.translate(TranslationKey.cannotUpdateTheParticipant)
                );
                return;
            }
        }

        let payByInvoice: boolean;
        if (!this.productResponse?.pay_by_invoice) {
            // Paying by invoice is not allowed by the server
            payByInvoice = false;
        } else if (shouldStartPaymentV2) {
            payByInvoice = shoppingCartV2ChangedData.selectedPayByOption === PayById.invoice;
        }

        if (this.multiRegistrationSvc.config?.workflow_id && !this.multiRegistrationSvc.hasError()) {
            const multiRegistrationResult = await this.multiRegistrationSvc.submitMultiRegistration();
            if (!multiRegistrationResult) {
                return;
            }
            // re-init multi-registration to get error from backend
            await this.multiRegistrationSvc.destroy();
            await this.multiRegistrationSvc.initController(
                this.eventId,
                {
                    ...this.processFormResponse.multiregistration,
                },
                () => this.getShoppingCartResultUI(),
            );
            if (this.hasFormErrors(true)) {
                return;
            }
        }

        let completeResponse: ICompleteProcessFormResponse;
        if (shouldStartPaymentV2) {
            await this.processShoppingCartV2Payment(
                shoppingCartV2ChangedData,
                productIdsWithQuantity,
                shoppingCartV2ChangedData.donationProducts,
                payByInvoice,
                this.eventDependencies ?? [],
                this.pageData.id
            );
        } else {
            // No payment is needed
            completeResponse = await this.apiSvc.completeProcessForm(this.pageData.id);
            await this.navigateToCompleteResponseNextPage(completeResponse);
        }
    }

    private createProductIdWithQuantities(productsWithQuantities: IProductWithQuantity[]): IProductIdWithQuantity[] {
        return productsWithQuantities.map(x => ({
            name: x.product.name,
            product_id: x.product.id,
            quantity: x.quantity,
            type: x.product.type
        } as IProductIdWithQuantity));
    }

    private async showTicketProductsNotSelectedPopUp(ticketProducts: IProductIdWithQuantity[]): Promise<void> {
        const dialogCfg: IModalDialogConfig = {
            firstButton: { id: 'ok', text: 'OK' },
            textLines: [
                this.appSvc.translate(TranslationKey.selectQuantityForAtLeastOneOfTheFollowingProducts),
                ...ticketProducts.map(x => '- ' + x.name)
            ]
        };
        await this.modalDialogSvc.showPopupDialog(dialogCfg);
        this.modalDialogSvc.hidePopupDialog();
    }

    async processShoppingCartV2Payment(
        shoppingCartV2ChangedData: IShoppingCartV2ChangedData,
        productIdsWithQuantity: IProductIdWithQuantity[],
        donationProducts: DonationProductItem[],
        payByInvoice: boolean,
        eventDependencies: IDependency[],
        pageId: string
    ): Promise<void> {
        const discountCode = shoppingCartV2ChangedData.discountCode || '';
        const payResponse = await this.apiSvc.payProductsV2(productIdsWithQuantity, donationProducts, eventDependencies, discountCode, payByInvoice);
        const paymentResult = await this.processPayV2Response(payResponse);
        if (paymentResult.redirectsExternally) {
            // The payment performs standard HTTP redirect - we don't need to call this.apiSvc.completeProcessForm
            return;
        }
        if (paymentResult.success) {
            const completeResponse = await this.apiSvc.completeProcessForm(pageId);
            await this.navigateToCompleteResponseNextPage(completeResponse);
        }
        await this.reloadDependencies();
    }

    async navigateToCompleteResponseNextPage(completeResponse: ICompleteProcessFormResponse): Promise<void> {
        if (completeResponse.next_page_id) {
            // There is next form - load id
            await this.loadEventPage(completeResponse.next_page_id);
        } else if (completeResponse.thank_you_page_id) {
            await this.loadEventPage(completeResponse.thank_you_page_id);
        } else {
            // There is no next form - show message after we navigate to home page
            this.shouldShowYourFormHasBeenSubmittedMessage = true;
            this.loadEventPage('');
        }
    }

    async triggerIntegrations(isForm?: boolean): Promise<void> {
        if (isForm) {
            await this.triggerTealiumEvent(TealiumEvent.formSubmit);
            this.triggerGoogleTagManagerEvent(GoogleTagManagerEvent.formSubmit);
        } else {
            await this.triggerTealiumEvent();
            this.triggerGoogleTagManagerEvent();
        }
    }

    async processPayResponse(response: IPayResponse): Promise<IPaymentResult> {
        return await this.usePaymentProvider(response);
    }

    async processPayV2Response(response: IPayResponse): Promise<IPaymentResult> {
        if (!response.is_checkout) {
            // Do not use payment provider
            return { success: true };
        }
        return await this.usePaymentProvider(response);
    }

    async usePaymentProvider(response: IPayResponse): Promise<IPaymentResult> {
        const result: IPaymentResult = { success: false };
        if (response.provider === PaymentProvider.cybersource) {
            const cybersourceSettings = response.settings as ICyberSourcePaySettings;
            this.domSvc.createAndPostPaymentForm(cybersourceSettings.redirect_url, cybersourceSettings.cybersource_form);
            result.redirectsExternally = true;
        } else if (response.provider === PaymentProvider.stripe) {
            try {
                const stripeSettings = response.settings as IStripePaySettings;
                const stripeRes = await this.processStripePayment(stripeSettings);
                result.success = true;
            } catch (err) {
                this.notificationSvc.showNotification(NotificationMessageType.error, err.message || err.error?.message || err);
            }
        } else if (response.provider === PaymentProvider.moneris) {
            const monerisSettings = response.settings as IMonerisPaySettings;
            const monerisPaymentResult = await this.processMonerisPayment(monerisSettings, response);
            result.success = monerisPaymentResult.success;
            result.redirectsExternally = true;
        } else if (response.provider === PaymentProvider.ingenico) {
            const ingenicoSettings = response.settings as IIngenicoPaySettings;
            await this.processIngenicoPayment(ingenicoSettings, response);
            result.redirectsExternally = true;
            result.success = true;
        } else if (response.provider === PaymentProvider.payOne) {
            const settings = response.settings as IPayOnePaySettings;
            await this.processPayOnePayment(settings, response);
            result.redirectsExternally = true;
            result.success = true;
        } else if (response.provider === PaymentProvider.saferpay) {
            const settings = response.settings as ISaferpaySettings;
            await this.processSaferpayPayment(settings, response);
            result.redirectsExternally = true;
            result.success = true;

        } else if (response.provider === PaymentProvider.invoice) {
            const invoiceSettings = response.settings as IInvoicePaySettings;
            window.location.href = invoiceSettings.redirect_url;
            result.success = true;
        } else if (response.provider === PaymentProvider.revere) {
            const revereSettings = response.settings as IReverePaySettings;
            this.revereCheckoutUrl = revereSettings.redirect_url;
            const reverePaymentResult = await this.processReverePayment(revereSettings, response);
            result.success = reverePaymentResult.success;
            // Revere payment logic redirects by following the response of form POST
            result.redirectsExternally = true;
        } else if (response.provider === PaymentProvider.mollie) {
            const mollieSettings = response.settings as IMolliePaySettings;
            if (!mollieSettings.checkout_url) {
                this.notificationSvc.showNotification(NotificationMessageType.error, 'Cannot perform the payment - redirect URL is empty');
            } else {
                window.location.href = mollieSettings.checkout_url;
                result.success = true;
            }
        } else {
            // Unknown payment provider
            const msg = this.appSvc.translate(TranslationKey.unknownPaymentProvider).replace('{{name}}', response.provider);
            this.notificationSvc.showNotification(NotificationMessageType.error, msg);
            result.success = false;
        }
        return result;
    }

    async processMonerisPayment(monerisPaySettings: IMonerisPaySettings, payResponse: IPayResponse): Promise<IMonerisPaymentResult> {
       const result: IMonerisPaymentResult = {
        success: false,
       };
        // The flag "success" currently returns the string "true" instead the boolean true
        // We have to check its value to determine whether the result is success or not
        if (monerisPaySettings.success === false || ('' + monerisPaySettings.success) === 'false') {
            this.appLoggerSvc.log(LogLevel.error, 'Moneris error', monerisPaySettings, payResponse);
            const msg = `${this.appSvc.translate(TranslationKey.paymentDidNotSucceed)} Moneris failed - check configuration`;
            this.notificationSvc.showNotification(NotificationMessageType.error, msg);
            result.success = false;
            return result;
        }
        const scriptSource = this.monerisSvc.getScriptLocation(monerisPaySettings.environment);
        await this.domSvc.loadScript(scriptSource);
        const checkout = new monerisCheckout();
        checkout.setMode(monerisPaySettings.environment);
        checkout.setCheckoutDiv('monerisCheckout');

        checkout.setCallback('payment_receipt', (responseString: string) => this.processMonerisPaymentReceiptArgs(checkout, responseString, payResponse));
        checkout.setCallback('cancel_transaction', (responseString: string) => this.processMonerisCancelTransaction(checkout, responseString, payResponse));
        checkout.setCallback('error_event', (args: any) => {
            this.appLoggerSvc.log(LogLevel.error, 'Moneris error', args);
            const msg = `${this.appSvc.translate(TranslationKey.paymentDidNotSucceed)} Moneris error event: ${args}`;
            this.notificationSvc.showNotification(NotificationMessageType.error, msg);
        });

        checkout.startCheckout(monerisPaySettings.ticket);

        // Success true means Moneris has been show, not if the payment is successful
        result.success = true;
        return result;
    }

    async processMonerisPaymentReceiptArgs(monerisCheckout: any, responseString: string, payResponse: IPayResponse): Promise<void> {
        const args = JSON.parse(responseString) as IMonerisPaymentArgs;
        if (args.response_code === MonerisPaymentResponseCode.success) {
            const paymentIntegrationWebhookReq: IPaymentIntegrationWebhookRequest = {
                ticket: args.ticket,
                order_id: payResponse.order_id,
                transaction_id: payResponse.transaction_id
            };
            try {
                const monerisPaySettings = payResponse.settings as IMonerisPaySettings;
                await this.apiSvc.paymentIntegrationWebhook(monerisPaySettings.integration_id, paymentIntegrationWebhookReq);
                // Give a cycle to Angular to set the values from this.monerisFormData to the form in the document
                // Wait 3 seconds before redirecting giving time for the user to acknowledge the payment was successfull
                const redirectTimeout = 3000;
                setTimeout(() => {
                    // monerisCheckout.closeCheckout();
                    window.location.href = this.apiUtilsSvc.getPaymentIntegrationRedirectUrl(
                        monerisPaySettings.integration_id, payResponse.transaction_id
                    );
                }, redirectTimeout);
            } catch (err) {
                this.appLoggerSvc.log(LogLevel.error, 'Moneris integration webhook error', err);
                this.notificationSvc.showNotification(NotificationMessageType.error, 'Payment integration failed');
                return;
            } finally {
                // Should we close the payment screen in case we have errors calling Azavista back-end ?
                //monerisCheckout.closeCheckout();
            }
        } else {
            const msg = `${this.appSvc.translate(TranslationKey.paymentDidNotSucceed)} (code = ${args.response_code})`;
            this.notificationSvc.showNotification(NotificationMessageType.error, this.appSvc.translate(TranslationKey.paymentDidNotSucceed));
        }
    }

    processMonerisCancelTransaction(monerisCheckout: any, responseString: string, payResponse: IPayResponse): void {
        const args = JSON.parse(responseString) as IMonerisPaymentArgs;
        monerisCheckout.closeCheckout();
        this.notificationSvc.showNotification(NotificationMessageType.warning, this.appSvc.translate(TranslationKey.transactionCancelled));
    }

    async processStripePayment(settings: IStripePaySettings): Promise<any> {
        const stripe = Stripe(settings.public_api_key);
        return stripe.redirectToCheckout({ sessionId: settings.session_id });
    }

    async processIngenicoPayment(settings: IIngenicoPaySettings, response: IPayResponse, options?: {
        customFailMessage?: string
    }): Promise<any> {
        if (!settings.isSuccess || !settings.redirectUrl) {
            this.notificationSvc.showNotification(NotificationMessageType.error, options?.customFailMessage || this.appSvc.translate(TranslationKey.ingenicoPaymentDidNotSucceed));
            return;
        }

        window.location.href = settings.redirectUrl;
    }

    async processPayOnePayment(settings: IPayOnePaySettings, response: IPayResponse): Promise<any> {
        const errorMessage = `${this.appSvc.translate(TRANSLATION_MAP.PAYONE_PAYMENT_DID_NOT_SUCCEED)} ${settings.errors?.map(text => this.appSvc.translate(text as any)).join() || ''}`.trim();
        this.processIngenicoPayment(settings, response, { customFailMessage: errorMessage});
    }

    async processSaferpayPayment(settings: ISaferpaySettings, response: IPayResponse): Promise<any> {
        const errorMessage = `${this.appSvc.translate(TRANSLATION_MAP.SAFERPAY_PAYMENT_DID_NOT_SUCCEED)} ${settings.errors?.map(text => this.appSvc.translate(text as any)).join() || ''}`.trim();

        if (settings.isSuccess === false || !settings.redirectUrl) {
            this.notificationSvc.showNotification(NotificationMessageType.error, errorMessage);
            return;
        }
        window.location.href = settings.redirectUrl;
    }

    async processReverePayment(settings: IReverePaySettings, response: IPayResponse): Promise<IReverePaymenetResult> {
        const result: IReverePaymenetResult = { cancelled: false, success: false };
        const additionalAttributes: IIdWithValue[] = [
            { id: RevereAttributeName.dataTokenizationKey, value: settings.tokenization_key },
            { id: RevereAttributeName.dataVariant, value: RevereDataVariantAttributeValue.inline },
            { id: RevereAttributeName.dataStyleSniffer, value: 'true' },
        ];
        await this.domSvc.loadScript('https://secure.reverepayments.com/token/Collect.js', additionalAttributes);
        CollectJS.configure({
            fields: {
                ccnumber: {
                    placeholder: '0000 0000 0000 0000'
                },
                ccexp: {
                    placeholder: '00 / 00'
                },
                cvv: {
                    placeholder: '***'
                },
            },
            validationCallback: (field: string, status: boolean, message: string) =>
                this.sharedSvc.executeInNgZone(() => this.processCollectJSValidation(field, status, message)),
            callback: (data: IReverePaymentResponse) =>
                this.sharedSvc.executeInNgZone(() => this.processCollectJSPaymentResponse(data)),
            timeoutCallback: () => this.sharedSvc.executeInNgZone(() => this.processCollectJSPaymentResponseTimeout()),
        });
        const fieldIsEmptyText = this.translateSvc.instant(TRANSLATION_MAP.FIELD_IS_EMPTY);
        this.revereFormFieldValidation = {
            ccnumber: { valid: false, message: fieldIsEmptyText },
            ccexp: { valid: false, message: fieldIsEmptyText },
            cvv: { valid: false, message: fieldIsEmptyText },
        };
        const shoppingCartV2ResultUI = this.getShoppingCartResultUI();
        this.revereDialogConfig = {
            currentOrderTotal: shoppingCartV2ResultUI.totals.current_order_total,
            cartCurrency: this.shoppingCartV2Cmp.cartCurrency,
        };
        this.revereCheckoutDialogResult = await this.revereCheckoutDialogSvc.showDialog(this.revereDialogConfig);
        if (!this.revereCheckoutDialogResult.proceed) {
            // Dialog cancel button is clicked
            this.revereCheckoutDialogSvc.hideDialog();
            result.cancelled = true;
            result.success = false;
            return result;
        }
        const invalidRevereFields = this.getInvalidRevereFormFields();
        if (invalidRevereFields.length > 0) {
            // Cannot proceed with invalid field values
            result.success = false;
            return result;
        }
        this.revereCheckoutDialogSvc.paymentStarted();
        // We are not going to close the dialog - the back-end will redirect for both success and failure
        // this.revereCheckoutDialogSvc.hideDialog();
        // This will cause sometime later the callback specified in CollectJS.configure to be executed (processCollectJSPaymentResponse)
        CollectJS.startPaymentRequest();
        // TODO: How to know whether it has succedded ?
        result.success = true;
        return result;
    }

    // onRevereFormSubmit(value: HTMLFormElement): void {
    // onRevereFormSubmit(): void {
    //     // this.revereForm = form;
    //     // This will cause sometime later the callback specified in CollectJS.configure to be executed (processCollectJSPaymentResponse)
    //     CollectJS.startPaymentRequest();
    // }

    processCollectJSValidation(field: string, status: boolean, message: string): void {
        this.revereFormFieldValidation[field] = { valid: status, message };
        const invalidFields = this.getInvalidRevereFormFields();
        this.revereCheckoutDialogSvc.processInvalidFields(invalidFields);
    }

    private getInvalidRevereFormFields(): IIdWithValue[] {
        const result: IIdWithValue[] = [];
        const keys = Object.keys(this.revereFormFieldValidation);
        for (const key of keys) {
            const record = this.revereFormFieldValidation[key];
            if (!record.valid) {
                result.push({ id: key, value: record.message });
            }
        }
        return result;
    }

    async processCollectJSPaymentResponseTimeout(): Promise<void> {
        // TODO: ?
        this.appLoggerSvc.log(LogLevel.error, 'Revere payment timed out');
    }

    async processCollectJSPaymentResponse(data: IReverePaymentResponse): Promise<void> {
        if (data?.token) {
            const revereForm: IRevereCheckoutForm = {
                email: this.revereCheckoutDialogResult.email,
                first_name: this.revereCheckoutDialogResult.firstName,
                last_name: this.revereCheckoutDialogResult.lastName,
                payment_token: data.token
            };
            this.domSvc.createAndPostPaymentForm(this.revereCheckoutUrl, { ...revereForm });
        } else {
            // TODO: An error occured ?
            this.appLoggerSvc.log(LogLevel.error, 'Revere payment did not provide token in the result');
        }
    }

    async setEventPageData(pageData: IEventPageResponse): Promise<ISetEventPageDataResult> {
        const result: ISetEventPageDataResult = { hasShoppingCartV2: false };
        this.productResponse = null;
        this.lastShoppingCartChangedData = null;
        this.croppedImagesMap = {};
        this.deletedImageFieldValuesMap = {};
        this.documentFieldsFilesMap = {};
        this.deletedDocumentFieldsFilesMap = {};
        this.domSvc.restoreActivityListComponentParent();
        this.domSvc.restoreActivitiesCartComponentParent();
        this.domSvc.restoreMyEventsContainerParent();
        this.domSvc.restoreShoppingCartComponentParent();
        this.domSvc.restoreShoppingCarV2tComponentParent();
        this.domSvc.restoreMultiRegistrationComponentParent();
        await Promise.all(Object.keys(pageData.translations).map(async langId => {
            const languageDependency = LANGUAGE_DEPENDENCY_RECORD[langId as SupportedLanguageIsoId];
            await languageDependency?.momentLocaleImportFn();
        }));
        const htmlWithReplacedMergetags = this.domSvc.replaceMergetags(
            pageData.html,
            this.eventParticipant, this.eventParticipantFields, pageData.event, this.eventFields, this.eventSettings, this.currentLanguage
        );
        this.domSvc.replaceTranslationsMergetags(
            pageData.translations,
            this.eventParticipant, this.eventParticipantFields, pageData.event, this.eventFields, this.eventSettings,
        );
        if (pageData.stylesheet_url) {
            try {
                // Clear the content and wait for CSS to load so the user does not see not styled HTML
                this.domSvc.setDynamicContent('');
                await this.domSvc.addPageThemeStylesheet(this.appSvc.prefixWithUrlForResources(pageData.stylesheet_url));
            } catch { }
        }
        this.domSvc.setDynamicContent(htmlWithReplacedMergetags);
        this.domSvc.removeElementsWithAttribute(AttributeName.azaRemoveThis);
        this.domSvc.cleanUpOldShoppingCartElements();
        const eventParticipantAndEventFields = [...this.eventParticipantFields, ...this.eventFields];
        this.domSvc.removeInputFieldsForMissingFields(eventParticipantAndEventFields);
        this.domSvc.setInputFieldsTooltipAndPlaceholder(eventParticipantAndEventFields, this.currentLanguage);
        // We can switch from page without profile header to a page that has one
        // So in order to properly show/hide profile buttons on page load, always manage their visibility
        // We will make this immediatelly after dynamic content is set, because initially both profile buttons (login and profile) and shown
        // and we want to immediatelly set their visibility. If we change the visibility later in this function, async calls made meanwhile will
        // delay changing buttons visibility - this could become visible for the user - both buttons visible for a second
        // and then one of them disappears
        this.manageProfileButtonsVisibility();
        this.domSvc.addDynamicContentElementStyle(pageData.css);
        // Make options of a multiselect toggleable by just clicking on them
        this.domSvc.attachScriptToMultiSelectOptions();
        if (!this.processFormResponse.form_id) {
            this.domSvc.setGoToActiveFormButtonsDisabledClass(this.currentLanguage);
        }
        this.domSvc.attachScriptToGoToPageButtons((pageId, queryParams) => this.processGoToPageButtonClicked(pageId, queryParams));
        this.domSvc.replaceRecaptchaElements();

        this.hasShoppingCartV2 = this.domSvc.hasShoppingCartV2MainContainerElement();

        const activityListContainerEl = this.domSvc.clearActivitiesElement();
        if (activityListContainerEl) {
            const moved = this.domSvc.moveActivityListComponentElementToDynamicActivityListElementPlaceholder();
            if (moved) {
                // Notify the component that it has been shown
                // This is necessary for it to perform initial actions (like searching activities)
                // Component's ngOnInit is called only once and the component is not controlled by *ngIf
                // That's why it can be shown multiple times (because user navigation) with a single ngOnInit and we need to notify it
                this.showActivitiesListSubject.next();
            }
        }
        this.domSvc.setActivitiesVisibility(!!activityListContainerEl);

        const myEventsDynamicContainerElementResult = this.domSvc.clearMyEventsDynamicContainerElement();
        const canShowMyEvent = this.getCanShowMyEvents();
        if (canShowMyEvent && myEventsDynamicContainerElementResult.contentElement) {
            const thisEventOnly = myEventsDynamicContainerElementResult.sectionElement.hasAttribute(AttributeName.azaMyEventsThisEventOnly);
            const skipEmailAddressStep = myEventsDynamicContainerElementResult.sectionElement.hasAttribute(AttributeName.azaSkipEmailAddressStep);
            const disableCompanyContacts = myEventsDynamicContainerElementResult.sectionElement.hasAttribute(AttributeName.azaMyEventsDisableCompanyContacts);

            this.eventListRestrictToEventId = thisEventOnly ? this.eventId : '';
            const moved = this.domSvc.moveMyEventsContainerElementToDynamicMyEventsElementPlaceholder();
            if (moved) {
                // The domSvc returns the element with attribute aza-my-events-list-content which is child of the
                // <section> element that contains the aza-dependency
                const companyManagerDependencyId = myEventsDynamicContainerElementResult.sectionElement.getAttribute(AttributeName.azaMyEventsListSectionCompanyManagerDependency)?.trim();
                await this.setMyEventsData({companyManagerDependencyId, thisEventOnly, skipEmailAddressStep, disableCompanyContacts});
            }
        }

        const activitiesCartContainerEl = this.domSvc.clearActivitiesCartElement();
        this.isActivitiesCartRequired = false;
        this.hasActivitiesCart = !!activitiesCartContainerEl;
        if (activitiesCartContainerEl) {
            const moved = this.domSvc.moveActivitiesCartComponentElementToDynamicActivitiesCartElementPlaceholder();
            if (moved) {
                this.activitiesCartDisplayType = this.domSvc.getActivitiesCartSectionDisplayType();
                this.activitiesCartShowAddToCalendar = this.domSvc.getActivitiesCartShowAddToCalendar();
                this.activitiesCartHideAvailablePlacesLeft = this.domSvc.getActivitiesCartHideAvailablePlacesLeft();
                this.getActivitiesCartResponse = await firstValueFrom(this.getActivitiesCart$);
                // The activitiesCartContainerEl is a <div> inside a <section> - the <section> is the one that has aza-container-required
                this.isActivitiesCartRequired = activitiesCartContainerEl.parentElement.hasAttribute(AttributeName.azaContainerRequired);
                this.isActivitiesCartLocked = activitiesCartContainerEl.parentElement.hasAttribute(AttributeName.azaActivitiesCartLocked);
                // We should not execute activities cart logic before the fields are filled with their values because dependency evaluation will be wrong
                // this.showActivitiesCartSubject.next(this.getActivitiesCartResponse);
            } else {
                this.getActivitiesCartResponse = null;
                // We should not execute activities cart logic before the fields are filled with their values because dependency evaluation will be wrong
                // this.showActivitiesCartSubject.next(null);
            }
        } else {
            this.getActivitiesCartResponse = null;
            // We should not execute activities cart logic before the fields are filled with their values because dependency evaluation will be wrong
            // this.showActivitiesCartSubject.next(null);
        }

        this.domSvc.setActivitiesCartVisibility(!!activitiesCartContainerEl);

        const publicCalendarTargetEl = this.domSvc.getPublicCalendarTargetElement();
        if (publicCalendarTargetEl) {
            this.domSvc.setPublicCalendarTargetElementUrl(publicCalendarTargetEl, window.location.origin);
            this.domSvc.clearElementInnerText(publicCalendarTargetEl);
            try {
                const publicCalendarScriptPath = '/w/public-calendar/calendar-widget-latest.js';
                await this.domSvc.loadScript(publicCalendarScriptPath);
                // Use the exposed object from the public calendar to initiate it with the public calendar target element
                interface IAzavistaPublicCalendarGlobalObject {
                    init: () => void;
                    initWithElement: (target: HTMLElement | null) => void;
                }
                interface IAzavistaPublicCalendarWindowWithGlobalObject extends Window {
                    __azavista_public_calendar?: IAzavistaPublicCalendarGlobalObject;
                }
                const azavistaPublicCalendarObj = (window as IAzavistaPublicCalendarWindowWithGlobalObject).__azavista_public_calendar;
                azavistaPublicCalendarObj.initWithElement(publicCalendarTargetEl);
            } catch (err) {
                const errMsg = 'Public calendar is used in the page content but the calendar script '
                    + 'does not expose the object named __azavista_public_calendar';
                this.appLoggerSvc.log(LogLevel.error, errMsg, err);
            }
        }

        this.domSvc.clearShoppingCartV1ProductsElement();
        this.domSvc.clearShoppingCartV2ContainerElement();
        const hasShoppingCartV1 = this.domSvc.hasShoppingCartV1MainContainerElement();
        const hasShoppingCartV2 = this.domSvc.hasShoppingCartV2MainContainerElement();
        result.hasShoppingCartV2 = hasShoppingCartV2;
        this.domSvc.setShoppingCartV1Visibility(hasShoppingCartV1);
        this.domSvc.setShoppingCartV2Visibility(hasShoppingCartV2);
        if (hasShoppingCartV1) {
            this.domSvc.moveShoppingCartV1ComponentElementToDynamicShoppingCartElementPlaceholder();
        }

        this.domSvc.replaceVideoElements();
        this.domSvc.replaceGoogleMapElements();
        this.domSvc.startCountdownSections(this.eventSettings.start);
        this.domSvc.setTranslations(pageData.translations, this.currentLanguage, this.eventParticipantFields);
        this.domSvc.attachToDocumentFields(this.eventParticipantFields, this.eventParticipant, data => this.processDocumentFieldCallbackData(data));
        this.domSvc.attachToImageFields(
            this.eventParticipantFields, data => this.processImageFieldCallbackData(data), (data) => this.removeImageCallback(data)
        );
        this.domSvc.setLockedFields(this.eventParticipantFields, this.eventParticipant);
        this.domSvc.setHrefImagesClickCallback((href, target) => this.processHrefImageClicked(href, target));
        this.domSvc.attachToHrefImages();
        await this.loadImageFieldsImages();
        this.showDownloadLinksToDocuments();
        this.setMenuItems(pageData);
        this.setButtonLinks(pageData);
        this.domSvc.attachToProfileButtons(() => this.profileLoginClicked(), () => this.toggleProfileMenu());

        return result;
    }

    async impersonateAnotherUserByEmail(email?: string, eventId?: string): Promise<void> {
        if (!email && !this.myEventsData.skipEmailAddressStep) {
            const result = await this.addToEventDialogSvc.showPopupDialog(null);
            this.addToEventDialogSvc.hidePopupDialog();
            if (!result) {
                return;
            }
            email = result.email;
        }

        try {
            const impersonateResponse = await this.apiSvc.impersonate(email);
            // If we don't have email provided - the server will generate one and will put it in the response
            // The response email will also contain the email that we sent with the response so set the email here
            email = impersonateResponse.email;
            // Now we need to open another tab providing query parameters:
            // page_id=aza-form
            // impersonation-data=...<base64 of impersonation token response and the email>...
            // use-session-storage=true
            // When the application detects use-session-storage=true it will configure the API service to switch to session token
            // so only the opened tab will have this new impersonation token
            // When the application detects impersonation-data it will decode it, put it in the storage (session storage because of use-session-storage=true)
            // and will start the flow by getting event token from identity (impersonation) token
            const url = new URL(window.location.toString());
            eventId ||= this.eventId;
            url.pathname = `/w/event/${eventId}/`;
            url.search = '';
            url.searchParams.append(SearchParamName.page_id, SpecialPageId.azaForm);
            url.searchParams.append(SearchParamName.useSessionStorage, 'true');
            const identityData: IStoredIdentityToken = { email: email, tokenResponse: impersonateResponse.impersonization_token };
            const base64ImpersonateData = window.btoa(JSON.stringify(identityData));
            url.searchParams.append(SearchParamName.identity, base64ImpersonateData);
            url.searchParams.append(SearchParamName.impersonated, 'true');
            url.searchParams.append('debug', 'true');
            this.navigateToHref(url.toString(), '_blank');
        } catch (err) {
            const msg = `Cannot impersonate email '${email}'`;
            this.notificationSvc.showNotification(NotificationMessageType.error, msg);
        }
    }

    private async setMyEventsData(params: Pick<IMyEventsData, 'companyManagerDependencyId' | 'thisEventOnly' | 'skipEmailAddressStep' | 'disableCompanyContacts'>): Promise<void> {
        const {
            companyManagerDependencyId,
            disableCompanyContacts,
            skipEmailAddressStep,
            thisEventOnly,
        } = params;
        const eventTokenIsOrganizationManager = this.eventTokenIsOrganizationManager();
        const columns = this.myEventsSvc.getMyEventsTableColumns();
        this.myEventsData.lastTableSettings = this.sharedSvc.createDefaultTableSettings('start', SortDirection.desc);
        this.myEventsData.isOrganizationManager = eventTokenIsOrganizationManager;
        this.myEventsData.companyManagerDependencyId = companyManagerDependencyId;
        this.myEventsData.thisEventOnly = thisEventOnly;
        this.myEventsData.restrictToEventId = thisEventOnly ? this.eventId : '';
        this.myEventsData.skipEmailAddressStep = skipEmailAddressStep;
        this.myEventsData.disableCompanyContacts = !!disableCompanyContacts;
        try {
            // Simulate settings changed so events are loaded and provide the columns
            await this.myEventsTableSettingsChanged(this.myEventsData.lastTableSettings, columns);
        } catch (err) {
            const errMsg = this.translateSvc.instant(TRANSLATION_MAP.CANT_LOAD_EVENTS_OF_CURRENT_PARTICIPANT);
            this.notificationSvc.showNotification(NotificationMessageType.warning, errMsg);
            this.appLoggerSvc.log(LogLevel.error, errMsg, err);
            // Provide empty data on error
            this.myEventsData.dataSubject.next({
                columns: columns,
                rowObjects: [],
                pagedResponse: {
                    limit: this.myEventsData.lastTableSettings.page.limit,
                    object_count: 0,
                    offset: this.myEventsData.lastTableSettings.page.offset
                },
                sortData: {
                    fieldName: this.myEventsData.lastTableSettings.sort.fieldName,
                    sortDirection: SortDirection.desc
                },
            });
        }
    }

    onMyEventsDateConversionToggled(toggled: boolean): void {
        if (toggled) {
            this.onMyEventsDateConversionChanged(DateConversion.myTime);
        } else {
            this.onMyEventsDateConversionChanged(DateConversion.objectTime);
        }
    }

    async onMyEventsDateConversionChanged(dateConversion: DateConversion): Promise<void> {
        this.myEventsData.dateConversion = dateConversion;
        // this.myEventsData.flags.showInEventTimeVisible = dateConversion === DateConversion.myTime;
        // this.myEventsData.flags.showInMyTimeVisible = dateConversion === DateConversion.objectTime;
        const eventsRes = this.myEventsSvc.setMyEventDatesByResponse(
            this.myEventsData.lastResponse,
            this.myEventsData.dateConversion,
            this.eventSettings.datetime_format,
            this.currentLanguage,
        );
        this.myEventsData.dataSubject.next({
            rowObjects: eventsRes.events,
            pagedResponse: eventsRes,
        });
    }

    async onMyEventsPageRequested(settings: ITableSettings): Promise<void> {
        await this.myEventsTableSettingsChanged(settings);
    }

    async onMyEventsSortRequested(settings: ITableSettings): Promise<void> {
        if (settings.sort.fieldName === 'crmstatus') {
            // CRM status is a special field and the events cannot be sorted by it yet
            return;
        }
        await this.myEventsTableSettingsChanged(settings);
    }

    async myEventsTableSettingsChanged(settings: ITableSettings, columns?: IField[]): Promise<void> {
        const eventTokenInfo = this.getCurrentEventTokenInfo();
        await this.myEventsSvc.settingsChanged(
            this.pageData.id,
            settings,
            this.myEventsData,
            this.currentLanguage,
            this.eventSettings.datetime_format,
            eventTokenInfo.payload.participant_id,
            eventTokenInfo.payload.contact_id,
            eventTokenInfo.payload.om,
            this.myEventsData.companyManagerDependencyResult,
            columns
        );
    }

    async downloadMyEvents() {
        const lastTableSettings = this.myEventsData?.lastTableSettings;
        if (lastTableSettings) {
            await this.myEventsSvc.downloadEvents(this.pageData.id, this.myEventsData.restrictToEventId, lastTableSettings);
        }
    }

    onMyEventsRowActionSelected(row: IProfileEvent): void {
        this.onAnotherEventClicked(row);
    }

    navigateToHref(href: string, target: string): void {
        if (target === '_blank') {
            window.open(href, target);
        } else {
            location.href = href;
        }
    }

    processHrefImageClicked(href: string, target?: string): void {
        if (href && href.trim()) {
            this.navigateToHref(href, target);
        }
    }

    showDownloadLinksToDocuments(): void {
        for (const field of this.eventParticipantFields) {
            const fieldType = this.utilsSvc.getFieldTypeFromField(field);
            if (fieldType === 'document') {
                const documentFileId = this.eventParticipant[field.id] || this.eventParticipant[field.name];
                if (documentFileId) {
                    this.domSvc.showDocumentDownloadLink(field.id);
                }
            }
        }
    }

    async loadImageFieldsImages(): Promise<void> {
        // Get download tokens and put URL to img elements
        for (const field of this.eventParticipantFields) {
            const fieldType = this.utilsSvc.getFieldTypeFromField(field);
            if (fieldType === 'image') {
                const imageDocumentFileId = this.eventParticipant[field.id] || this.eventParticipant[field.name];
                if (imageDocumentFileId) {
                    try {
                        const docTokenRes = await this.apiSvc.getDocumentDownloadToken(imageDocumentFileId);
                        const imageSrc = this.apiSvc.getDocumentFileDownloadUrl(imageDocumentFileId, docTokenRes.token);
                        this.domSvc.setImageFieldImageSource(field.id, imageSrc);
                    } catch (err) {
                        const cannotUploadImageForFiledTranslated = this.appSvc.translate(TranslationKey.cannotUploadImageForField);
                        this.notificationSvc.showNotification(NotificationMessageType.error, `${cannotUploadImageForFiledTranslated} ${field.label}`);
                    }
                    // try {
                    //   const documnetBlob = await this.apiSvc.getDocumentFile(imageDocumentFileId);
                    //   const objectUrl = URL.createObjectURL(documnetBlob);
                    //   const imgSrc = this.domSanitizer.bypassSecurityTrustResourceUrl(objectUrl) as any;
                    //   this.domSvc.setImageFieldImageSource(field.id, imgSrc);
                    // } catch (err) {
                    //   this.notificationSvc.showNotification(NotificationMessageType.error, `Can't load image for field ${field.label}`);
                    // }
                }
            }
        }
    }

    private getImageFieldWrapperFromChildElement(childEl: HTMLElement): HTMLElement | null {
        const imageFieldEl = this.domSvc.getImageFieldTypeParent(childEl);
        // The "aza-input-required" is on the parent of the parent of the aza-image-field element
        const imageFieldWrapperEl = imageFieldEl?.parentElement?.parentElement;
        return imageFieldWrapperEl;
    }

    removeImageCallback(data: IImageFieldCallbackData): void {
        this.resetImageRelatedProperties();
        delete this.croppedImagesMap[data.fieldId];
        this.deletedImageFieldValuesMap[data.fieldId] = true;
        this.validateImageField(data.fieldId, this.getImageFieldWrapperFromChildElement(data.event.target as HTMLElement));
    }

    resetImageRelatedProperties(): void {
        this.cropperImageChangeEvent = null;
        this.croppedImageBase64 = '';
        this.imgFieldSrc = null;
    }

    async processImageFieldCallbackData(data: IImageFieldCallbackData): Promise<void> {
        this.resetImageRelatedProperties();
        const dialogConfig: IImageCropperDialogConfig = {
            imageFileSelectedSubject: new Subject<IImageCropperImageFileSelectedEventArgs>(),
            cropButtonClickedSubject: new Subject<void>(),
            fieldId: data.fieldId
        };
        const imageField = this.eventParticipantFields.find(x => x.id === data.fieldId);
        if (!imageField) {
            const cannotFindFieldWithIdTranslated = this.appSvc.translate(TranslationKey.cannotFindFieldWithId);
            this.notificationSvc.showNotification(NotificationMessageType.error, `${cannotFindFieldWithIdTranslated} ${data.fieldId}`);
            return;
        }
        const imageFieldOptions = imageField.options as IImageFieldOptions;
        this.cropperAspectRatio = imageFieldOptions.aspect_ratio || 1;
        this.cropperMaxWidth = imageFieldOptions.maxWidth || 0;
        this.cropperMaxHeight = imageFieldOptions.maxHeight || 0;
        this.showCropButton = !imageFieldOptions.cropping_mandatory;
        let file: File | undefined = undefined;
        const imageFileSelectedSubjectSubscription = dialogConfig.imageFileSelectedSubject.subscribe({
            next: fileSelectedEvent => {
                this.processCropperImageFileSelected(fileSelectedEvent, fileResult => file = fileResult );
            }
        });
        const cropButtonClickedSubscription = dialogConfig.cropButtonClickedSubject.subscribe({
            next: () => this.processCropperCropButtonClicked()
        });
        this.croppingFieldId = data.fieldId;
        const dialogResult = await this.cropperDialogSvc.showCropperPopupDialog(dialogConfig);
        this.cropperDialogSvc.hidePopupDialog();
        imageFileSelectedSubjectSubscription.unsubscribe();
        cropButtonClickedSubscription.unsubscribe();
        if (dialogResult && this.croppedImageBytes) {
            delete this.errorFormFields[data.fieldId];
            this.croppedImagesMap[data.fieldId] = {
                buffer: this.croppedImageBytes.slice(),
                file
            };
            delete this.deletedImageFieldValuesMap[data.fieldId];
            this.croppedImageBase64 ||= 'data:image/png;base64,' + this.bufferToBase64(this.croppedImageBytes);
            this.domSvc.setImageFieldImageSource(data.fieldId, this.croppedImageBase64);
        }
        this.validateImageField(data.fieldId, this.getImageFieldWrapperFromChildElement(data.event.target as HTMLElement));
    }

    private validateImageField(fieldId: string, fieldWrapperEl: HTMLElement): void {
        const field = this.getFieldById(fieldId);
        const validationResult = this.validatorSvc.validateField(field, fieldWrapperEl);
        this.fieldValidated(field.id, fieldWrapperEl, validationResult);
    }

    bufferToBase64(buffer: Uint8Array): string {
        let binary = '';
        const len = buffer.length;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(buffer[i]);
        }
        return window.btoa(binary);
    }

    processCropperCropButtonClicked(): void {
        this.showCropper = true;
    }

    async processCropperImageFileSelected(args: IImageCropperImageFileSelectedEventArgs, fileCallbackFn: (file: File | undefined) => void): Promise<void> {
        const files = (args.event.target as HTMLInputElement).files;
        if (files.length === 0) {
            fileCallbackFn(undefined);
            return;
        }
        // // this.showImageInEditMode = false;
        this.cropperImageChangeEvent = args.event;
        const field = this.eventParticipantFields.find(x => x.id === args.fieldId);
        const imageFieldOptions = field.options as IImageFieldOptions;
        fileCallbackFn(files[0]);
        if (imageFieldOptions.cropping_mandatory) {
            // cropping is mandatory - show the cropper
            this.showCropper = true;
        } else {
            this.showCropper = false;
            this.showCropButton = true;
            // Cropping is not mandatory - read file bytes and show the image
            this.croppedImageBytes = await this.getImageFileBytes(files[0]);
            const objectUrl = URL.createObjectURL(new Blob([files[0]]));
            this.imgFieldSrc = this.domSanitizer.bypassSecurityTrustResourceUrl(objectUrl) as any;
        }
    }

    async getImageFileBytes(file: File): Promise<Uint8Array<ArrayBuffer>> {
        return new Promise((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.onload = () => {
                const arrayBuffer = fileReader.result as ArrayBuffer;
                const bytes = new Uint8Array(arrayBuffer);
                resolve(bytes);
            };
            fileReader.readAsArrayBuffer(new Blob([file]));
        });
    }

    /**
    * Resize image while also maintain aspect ratio.
    *
    * If target's `width` or `height` is `0` then it will return the original image
    *
    * @returns local image URL
    */
    async getResizedImage(imageSrc: string, target: ResizeTarget) {
        return new Promise<string>((resolve) => {
            const imgEl = new Image();
            imgEl.onload = () => {
                const { naturalWidth, naturalHeight } = imgEl;
                const scaleByWidthRatio = target.width / naturalWidth;
                const scaleByHeightRatio = target.height / naturalHeight;

                const isResizeByHeight = scaleByHeightRatio < scaleByWidthRatio;
                const scaleToResize = isResizeByHeight ? scaleByHeightRatio : scaleByWidthRatio;
                const isUpscale = scaleToResize >= 1;
                if (isUpscale || scaleToResize === 0) {
                    resolve(imageSrc);
                }
                const resizedData: string = isResizeByHeight
                    ? resizeImageToUrl(imgEl, naturalWidth * scaleToResize, target.height)
                    : resizeImageToUrl(imgEl, target.width, naturalHeight * scaleToResize);
                resolve(resizedData);
            };
            imgEl.src = imageSrc;
        });
    }

    async processDocumentFieldCallbackData(data: IDocumentFieldCallbackData): Promise<void> {
        if (data.clickedItem === DocumentFieldCallbackDataClickedItemName.inputField) {
            // Input field for file selection was clicked - add data to files map
            this.documentFieldsFilesMap[data.fieldId] = data;
            delete this.deletedDocumentFieldsFilesMap[data.fieldId];
            this.forceFieldValidation(data.fieldId, data.event.target as HTMLElement);
        } else if (data.clickedItem === DocumentFieldCallbackDataClickedItemName.downloadLinkContainer) {
            const docId = this.eventParticipant[data.fieldId];
            if (docId) {
                const downloadTokenRes = await this.apiSvc.getDocumentDownloadToken(docId);
                const url = this.apiSvc.getDocumentFileDownloadUrl(docId, downloadTokenRes.token);
                this.navigateToHref(url, '_blank');
            }
        } else if (data.clickedItem === DocumentFieldCallbackDataClickedItemName.deleteElement) {
            delete this.documentFieldsFilesMap[data.fieldId];
            this.deletedDocumentFieldsFilesMap[data.fieldId] = true;
            this.forceFieldValidation(data.fieldId, data.event.target as HTMLElement);
        } else if (data.clickedItem === DocumentFieldCallbackDataClickedItemName.reset) {
            delete this.documentFieldsFilesMap[data.fieldId];
            delete this.deletedDocumentFieldsFilesMap[data.fieldId] ;
            this.forceFieldValidation(data.fieldId, data.event.target as HTMLElement);
        }
    }

    private forceFieldValidation(fieldId: string, htmlEl: HTMLElement): void {
        const field = this.getFieldById(fieldId);
        if (field) {
            // Simulate field validation, because document fields does not have oninput="azavista.validateField(..." attribute
            const validationResult = this.validatorSvc.validateField(field, htmlEl);
            this.fieldValidated(field.id, htmlEl, validationResult);
        }
    }

    private getFieldById(fieldId: string): IField | null {
        const participantField = this.eventParticipantFields.find(x => x.id === fieldId);
        if (participantField) {
            return participantField;
        }
        const eventField = this.eventFields.find(x => x.id === fieldId);
        if (eventField) {
            return eventField;
        }
        return null;
    }

    processGoToPageButtonClicked(pageId: string, additionalQueryParams: QueryParamsInput | null): void {
        // if (!this.processFormResponse.form_id && pageId === SpecialPageId.azaForm) {
        //     return;
        // }
        // If page id is 'aza-form', get page id from form response
        const requestedPageId = pageId === SpecialPageId.azaForm ? (this.processFormResponse.form_id || pageId) : pageId;
        if (additionalQueryParams) {
            const searchParams = new URLSearchParams(additionalQueryParams);
            searchParams.forEach((value, id) => {
                this.appSvc.storeSearchParamInUrl(id, value);
            });
            this.queryParams = this.handleQueryParams();
        }
        this.loadEventPage(requestedPageId);
    }

    setMenuItems(pageData: IEventPageResponse): void {
        const currentTranslations = pageData.translations[this.currentLanguage]
            || pageData.translations[this.defaultLanguage]
            || pageData.translations['en-GB']
            || {};
        this.domSvc.setMenuItems(
            this.eventMenuItems,
            currentTranslations,
            pageData,
            (ev: Event, menuPageId?: string) => this.menuItemClicked(ev, menuPageId)
        );
    }

    async menuItemClicked(ev: Event, pageId?: string): Promise<void> {
        ev.preventDefault();
        if (pageId) {
            this.loadEventPage(pageId);
        }
    }

    setButtonLinks(pageData: IEventPageResponse): void {
        const currentTranslations = pageData.translations[this.currentLanguage] || pageData.translations[this.defaultLanguage];
        this.domSvc.sectionButtons(
            currentTranslations,
            (ev: Event, pageId?: string) => this.sectionButtonClicked(ev, pageId)
        );
    }

    async sectionButtonClicked(ev: Event, pageId?: string): Promise<void> {
        ev.preventDefault();
        if (pageId) {
            this.loadEventPage(pageId);
        }
    }

    // logOut(): void {
    //   this.reset();
    //   // TODO: What if access is public ?
    //   this.startLoginProcces();
    // }

    // reset(): void {
    //     this.appSvc.removeSearchParamFromUrl(SearchParamName.page_id);
    //     this.storageSvc.removeLanguage();
    //     // this.apiSvc.removeStoredIdentityToken(this.email);
    //     // this.apiSvc.removeStoredEventToken(this.eventId);
    //     // this.apiSvc.clearLastEventId();


    //     this.socialWallDisplay = 'none';

    //     // this.eventTokenResponse = null;
    //     this.processFormResponse = null;
    //     this.loginStep = LoginStep.sendEmail;
    //     this.storedIdentityToken = null;
    //     // this.storedEventTokens = [];
    //     this.eventDependencies = null;
    //     this.eventParticipantFields = null;
    //     this.eventParticipant = null;
    //     this.eventMenuItems = null;
    //     this.pageData = {} as any;
    //     this.croppedImagesMap = {};
    //     this.deletedImageFieldValuesMap = {};
    //     this.documentFieldsFilesMap = {};
    //     this.deletedDocumentFieldsFilesMap = {};
    //     this.email = '';
    //     this.username = '';
    //     this.registrationCode = '';
    //     this.linkTokenInfo = null;

    //     this.domSvc.setDynamicContent('');
    // }

    switchToSendEmailLoginStep(): void {
        this.loginStep = LoginStep.sendEmail;
        this.domSvc.showElement(this.loginPageDomSvc.getEmailFieldContainer());
        this.domSvc.hideElement(this.loginPageDomSvc.getRegistrationCodeFieldContainer());
    }

    // private switchToSendRegistrationCodeLoginStep(): void {
    //   this.loginStep = LoginStep.sendRegistrationCode;
    //   this.domSvc.hideElement(this.loginPageDomSvc.getEmailFieldContainer());
    //   this.domSvc.showElement(this.loginPageDomSvc.getRegistrationCodeFieldContainer());
    // }

    // private switchToPostRegistrationCodeLoginStep(): void {
    //   this.domSvc.hideElement(this.loginPageDomSvc.getLoginForm());
    // }

    attachToLoginSubmit(func: (ev: Event) => void): void {
        const loginBtn = this.loginPageDomSvc.getSubmitButton();
        loginBtn.onclick = func;
    }

    async languageChanged(newLanguage: string, storeLanguageInStorage: boolean): Promise<void> {
        this.currentLanguage = newLanguage;
        // If the language is not available (the server returns 404) current language remains active
        // In case of error on switching to the new language - use the default one
        this.translateSvc.use(this.currentLanguage).subscribe({
            error: err => this.translateSvc.use(this.defaultLanguage)
        });
        if (storeLanguageInStorage) {
            this.storageSvc.storeLanguage(newLanguage);
        }
        this.domSvc.setTranslations(this.pageData.translations, newLanguage, this.eventParticipantFields);
        this.domSvc.setSelectedLanguageItem(this.currentLanguage);
        this.domSvc.setInputFieldsTooltipAndPlaceholder([...this.eventParticipantFields, ...this.eventFields], this.currentLanguage);
        this.domSvc.setDocumentLanguageAndDirectionAttributes(this.currentLanguage);
        await this.loadLanguageDependency();
        if (this.myEventsData.lastResponse) {
            this.onMyEventsDateConversionChanged(this.myEventsData.dateConversion);
        }
    }

    changeActivityCartItemsVisibilityByDependencies(applyDepndenciesResult: IApplyDependenciesResult): void {
        // Simply emit the data - the activity cart component will use the value to change visibility
        this.subjectsSvc.getDependenciesAppliedSubject().next(applyDepndenciesResult);
    }

    changeProductsVisibilityByDependencies(applyDependenciesResult: IApplyDependenciesResult): void {
        const products = this.productResponse?.products;
        if (!applyDependenciesResult || !products) {
            this.shoppingCartProductsVisibility = [];
            return;
        }

        const productsVisibility = this.appSvc.getProductVisibilitiesByDependencyResults(
            applyDependenciesResult.dependencyResults,
            products
        );
        this.shoppingCartProductsVisibility = productsVisibility;
    }

    async fieldValidated(
        fieldId: string,
        element: HTMLElement,
        validationResult: IValidationResult,
        errorsContainerElementId?: string,
        inputFieldParentEl?: HTMLElement): Promise<void> {
        await this.reloadDependencies();
        if (!validationResult) {
            return;
        }
        inputFieldParentEl ||= this.domSvc.getInputFieldParent(element);
        if (!inputFieldParentEl) {
            return;
        }
        this.manageErrorFormFields(fieldId, validationResult);
        this.updatedEventParticipant = {
            ...this.updatedEventParticipant,
            [fieldId]: validationResult.value,
        };
        if (errorsContainerElementId) {
            this.domSvc.setValidationResultToInputElement(
                fieldId,
                element,
                validationResult,
                [...this.eventParticipantFields, ...this.eventFields],
                errorsContainerElementId,
                inputFieldParentEl,
            );
        }
    }

    manageErrorFormFields(fieldId: string, validationResult: IValidationResult): void {
        if (validationResult.isValid) {
            // The field is valid - remove it from the error fields
            delete this.errorFormFields[fieldId];
        } else {
            // The field is invalid - add it to error fields list
            this.errorFormFields[fieldId] = true;
        }
    }

    hasFormErrors(showNotification?: boolean): boolean {
        const hasMainFormErrors = Object.keys(this.errorFormFields).filter(f => {
            if (this.hiddenFieldIds.includes(f)) {
                return false;
            }
            return true;
        }).length > 0;
        const hasMultiRegistrationFormUnsavedChanges = this.multiRegistrationFormSvc.getHasEditedDraft();
        const hasMultiRegistrationFormGlobalErrors = !!this.multiRegistrationSvc.controller?.hasGlobalErrors();
        const hasMultiRegistrationFormParticipantErrors = !!this.multiRegistrationSvc.controller?.hasParticipantErrors();
        this.appLoggerSvc.log(LogLevel.log, 'hasFormErrors()', {
            hasMainFormErrors,
            hasMultiRegistrationFormGlobalErrors,
            hasMultiRegistrationFormParticipantErrors,
            errorFormFields: this.errorFormFields,
            multiRegistrationState: this.multiRegistrationSvc.uiState,
        });
        if (showNotification) {
            if (hasMainFormErrors) {
                this.notificationSvc.showNotification(
                    NotificationMessageType.error, this.appSvc.translate(TranslationKey.oneOrMoreMandatoryFieldsHaveNotBeenCompleted)
                );
            } else if (hasMultiRegistrationFormUnsavedChanges) {
                this.notificationSvc.showNotification(
                    NotificationMessageType.error, this.appSvc.translate(TRANSLATION_MAP.PLEASE_SAVE_UNSAVED_MULTIREGISTRATION_DATA)
                );
            } else if (hasMultiRegistrationFormGlobalErrors) {
                this.notificationSvc.showNotification(
                    NotificationMessageType.error, this.multiRegistrationSvc.getGlobalErrorMessage()
                );
            } else if (hasMultiRegistrationFormParticipantErrors) {
                const uiState = this.multiRegistrationSvc.uiState;
                const allSubParticipants = (uiState && getAllSubParticipants(uiState)) ?? [];
                const subParticipantWithErrorIndex = allSubParticipants.findIndex(subParticipant => subParticipant.error?.code);
                const subParticipantWithError = allSubParticipants[subParticipantWithErrorIndex];
                this.notificationSvc.showNotification(
                    NotificationMessageType.error,
                    `${this.sharedSvc.translate(
                        subParticipantWithError?.is_main ? TRANSLATION_MAP.MAIN_PARTICIPANT : TRANSLATION_MAP.SUB_PARTICIPANT
                    )} #${subParticipantWithErrorIndex + 1}: ${subParticipantWithError.error?.message}`
                );
            }
        }
        return hasMainFormErrors || hasMultiRegistrationFormUnsavedChanges || hasMultiRegistrationFormGlobalErrors || hasMultiRegistrationFormParticipantErrors;
    }

    processApiError(err: IErrorNotification): void {
        if (this.appSvc.isApiErrorInvalidEventToken(err)) {
            // Clear token for current event and reload
            this.apiSvc.removeStoredEventToken(this.eventId);
            window.location.reload();
            return;
        }

        try {
            const msg = this.appSvc.getErrorText(err);
            this.notificationSvc.showNotification(NotificationMessageType.error, msg);
        } catch (err) {
            this.notificationSvc.showNotification(NotificationMessageType.error, err.toString());
        }
    }

    triggerGoogleTagManagerEvent(gtmEventName?: string): void {
        if (!this.pageData || !(window as any).dataLayer) {
            return;
        }
        const prefix = 'classevents_';
        const dataLayerObject = {
            event: gtmEventName || GoogleTagManagerEvent.pageView,
            [`${prefix}event_id`]: this.pageData.event.id,
            [`${prefix}page_name`]: this.pageData.name,
            [`${prefix}page_id`]: this.pageData.id
        } as any;
        if (this.eventFields) {
            const eventFieldsData = this.appSvc.formatPageDataFields(this.pageData.event, this.eventFields);
            eventFieldsData.forEach(x => dataLayerObject[`${prefix}${x.name}`] = this.pageData.event[x.id]);
        }
        if (this.eventParticipantFields) {
            const participantFieldsData = this.appSvc.formatPageDataFields(this.pageData.participant, this.eventParticipantFields);
            participantFieldsData.forEach(x => dataLayerObject[`${prefix}participant_${x.name}`] = this.pageData.participant[x.id]);
        }
        this.domSvc.pushDataToGoogleTagManager(dataLayerObject);
    }

    triggerTealiumEvent(tealiumEventName?: string): void {
        const tmp = tealiumEventName ? tealiumEventName : 'page';
        this.appLoggerSvc.log(
            LogLevel.log,
            'Tealium trigger: ' + tmp
        );
        if (!this.pageData || !this.eventSettings) {
            return;
        }
        const utag = (window as any).utag;
        if (!utag) {
            return;
        }
        const endressSiteCountryField = this.isEndressTealiumImplementation();
        const dataObject = this.getTealiumDataObject(tealiumEventName, endressSiteCountryField);
        const fieldPrefix = 'site_eventclass_';
        if (this.eventFields) {
            const eventFieldsData = this.appSvc.formatPageDataFields(this.pageData.event, this.eventFields);
            eventFieldsData.forEach(x => dataObject[`${fieldPrefix}${x.name}`] = this.pageData.event[x.id]);
        }
        if (this.eventParticipantFields) {
            const participantFieldsData = this.appSvc.formatPageDataFields(this.pageData.participant, this.eventParticipantFields);
            participantFieldsData.forEach(x => dataObject[`${fieldPrefix}participant_${x.name}`] = this.pageData.participant[x.id]);
        }
        window['utag_data'] = dataObject;
        if (tealiumEventName === TealiumEvent.pageView || endressSiteCountryField) {
            utag.view(dataObject); // page-load data is sent to Tealium
        }
        if (tealiumEventName === TealiumEvent.formSubmit && !endressSiteCountryField) {
            utag.link(dataObject); // form-submit data is sent to Tealium
        }
    }

    getProfilePageIdQueryParams(): Record<string, string> {
        let queryParams: Record<string, string>;
        if (this.profilePageResponse?.page?.id) {
            queryParams = { profile_page_id: this.profilePageResponse?.page?.id };
        }
        return queryParams;
    }

    getTealiumDataObject(tealiumEventName: string, endressSiteCountryField?: string): any {
        const prefix = 'site_';
        let dataObject: any;
        if (endressSiteCountryField) {
            const isTealiumFormEvent = tealiumEventName === TealiumEvent.formSubmit;
            dataObject = {
                app_name: 'azavista',
                [`${prefix}country`]: endressSiteCountryField,
                [`${prefix}language`]: this.currentLanguage.split('-')[0],
                [`${prefix}h1`]: this.eventSettings.name,
                [`${prefix}h2`]: this.pageData.name,
                // [`${prefix}h3`]: processForm.workflow_type,
            };
            if (this.tealiumRegistration) {
                dataObject.goal_register_successful = 'register_successful';
            }
            this.tealiumRegistration = false;
            dataObject.page_name = Object.values(dataObject).map(x => x.toString().toLowerCase()).join('.');
            if (isTealiumFormEvent) {
                dataObject.goal_form_success = 'form_successful';
                const registrationWorkflowTypes = this.appSvc.getRegistrationWorkflowTypes();
                if (isTealiumFormEvent
                    && registrationWorkflowTypes.includes(
                        this.processFormResponse.workflow_type
                    )) {
                    this.tealiumRegistration = true;
                }
            }
        } else {
            dataObject = {
                event_name: tealiumEventName || TealiumEvent.pageView,
                app_name: 'azavista3.0',
                [`${prefix}domain`]: (window as any).location.hostname,
                [`${prefix}language`]: this.currentLanguage,
                [`${prefix}name`]: this.eventSettings.name,
                [`${prefix}id`]: this.pageData.event.id,
                [`${prefix}page_name`]: this.pageData.name,
                [`${prefix}page_id`]: this.pageData.id
            };
        }
        return dataObject;
    }

    isEndressTealiumImplementation(): string {
        const endressDistinctiveField = this.eventFields.find(x => x.name === 'site_endress_country');
        if (!this.pageData.event || !endressDistinctiveField) {
            return;
        }
        if (this.pageData.event[endressDistinctiveField.id]) {
            return this.pageData.event[endressDistinctiveField.id];
        }

        return 'not_set';
    }

    profileLoginClicked(): void {
        this.domSvc.hideBodyScrolls();
        this.profileFlags.showLogin = true;
    }

    async onAccountReset(args: IAccountResetArgs): Promise<void> {
        this.domSvc.hideBodyScrolls();
        this.profileFlags.showLogin = false;
        const getIdentityTokenResponse = await this.apiSvc.getIdentityToken({
            email: args.email,
            registrationCode: args.emailVerificationCode
        }, true);
        const getEventTokenRes = await this.tokenGetEventToken();
        if (!getEventTokenRes.token.participant_id) {
            // If the token does not contain participant id
            // we need to get profile page and use it later as query parameter profile_page_id to /w/page_content
            this.profilePageResponse = await this.apiSvc.getProfilePage();
        } else {
            // The token has participant_id - add profile_page_id query parameter to /w/page_content
        }
        // Show profile button if logged in successfully
        this.manageProfileButtonsVisibility();
        // After obtaining new tokens reload current page
        await this.loadEventPage(this.pageData.id);
    }

    async onLoggedIn(response: IGetEventTokenResponse): Promise<void> {
        this.setEventTokenReponse(response);
        // const pageId = this.appSvc.getSearchParamFromUrl(SearchParamName.page_id);
        this.hideAllProfileControls();
        this.domSvc.restoreBodyScrolls();
        this.manageProfileButtonsVisibility();
        // TODO: Do we also need to check for response.token.contact_id ?
        if (!response.token.participant_id) {
            // If the token does not contain participant id
            // we need to get profile page and use it later as query parameter profile_page_id to /w/page_content
            this.profilePageResponse = await this.apiSvc.getProfilePage();
        } else {
            // The token has participant_id - add profile_page_id query parameter to /w/page_content
        }
        // if (pageId) {
        //   this.loadEventPage(pageId, false);
        // } else {
        //   this.loadEventHomePage(false);
        // }
        await this.startupFlowCheckIdentityTokenExistence();
    }

    async onAnotherEventClicked(evt: IProfileEvent): Promise<void> {
        if (evt.settings?.publish !== EventPublish.noRegistration && evt.settings?.publish !== EventPublish.published) {
            // This event is closed
            await this.modalDialogSvc.showPopupDialog(
                {
                    firstButton: { id: 'ok', text: 'OK' },
                    textLines: ['This event is closed']
                }
            );
            this.modalDialogSvc.hidePopupDialog();
            return;
        }

        // Check if current user is organization manager
        if (this.eventTokenIsOrganizationManager()) {
            // The contact_id values of the selected event and the current user should be different - this means this is event for another participant
            const eventTokenInfo = this.getCurrentEventTokenInfo();
            if (eventTokenInfo.payload.contact_id !== evt.contact_id) {
                this.impersonateAnotherUserByEmail(evt.email, evt.id);
                return;
            }
        }

        // Obtain token for the new event using current identity token and reload browser to the new event id
        const res = await this.getEventTokenUsingCurrentIdentityToken(evt.id);
        if (res) {
            window.location.href = window.location.origin + '/w/event/' + encodeURIComponent(evt.id) + '/';
        }

    }

    toggleProfileMenu(): void {
        this.profileFlags.showSideMenu = !this.profileFlags.showSideMenu;
    }

    sideMenuItemClicked(item: ISideMenuItem): void {
        this.profileFlags.showSideMenu = false;
        this.hideAllProfileControls();
        this.domSvc.hideBodyScrolls();
        if (item.id === SideMenuItemId.events) {
            this.eventType = EventType.event;
            this.profileFlags.showEvents = true;
        } else if (item.id === SideMenuItemId.requests) {
            this.eventType = EventType.workflow;
            this.profileFlags.showEvents = true;
        } else if (item.id === SideMenuItemId.changePassword) {
            this.profileFlags.showChangePassword = true;
        } else if (item.id === SideMenuItemId.subscriptions) {
            this.profileFlags.showSubscriptions = true;
        } else if (item.id === SideMenuItemId.myProfile) {
            this.profileFlags.showProfile = true;
        } else if (item.id === SideMenuItemId.myOrders) {
            this.profileFlags.showOrders = true;
        } else if (item.id === SideMenuItemId.myOrganization) {
            this.profileFlags.showContacts = true;
        } else if (item.id === SideMenuItemId.logout) {
            this.apiSvc.removeStoredEventToken(this.eventId);
            this.apiSvc.removeStoredIdentityToken();
            window.location.reload();
        }
    }

    onProfileControlClosed(): void {
        this.hideAllProfileControls();
        this.domSvc.restoreBodyScrolls();
    }

    hideAllProfileControls(): void {
        this.profileFlags.showEvents = false;
        this.profileFlags.showChangePassword = false;
        this.profileFlags.showSubscriptions = false;
        this.profileFlags.showProfile = false;
        this.profileFlags.showOrders = false;
        this.profileFlags.showLogin = false;
        this.profileFlags.showContacts = false;
    }

    async getProductsForDependency(): Promise<IPaymentProduct[]> {
        if (this.hasShoppingCartV2
            && this.lastShoppingCartChangedData
        ) {
            return this.lastShoppingCartChangedData?.productsWithQuantity
                .filter(({ quantity }) => quantity > 0)
                .map(({ product }) => product) ?? [];
        }
        const allOrderProducts = this.order?.transactions?.reduce((result, { items }) => {
            items.forEach(({ quantity, product }) => {
                if (quantity > 0 && product.id) {
                    result.push(product as IPaymentProduct);
                }
            });
            return result;
        }, [] as IPaymentProduct[]);
        return allOrderProducts ?? [];
    }

    getWorkflowDependency(predefinedValue?: Partial<Workflow>): Workflow {
        const { payment, payment_method, waitlist } = predefinedValue ?? {};
        const { payment_flow, reservation_type } = this.processFormResponse ?? {};
        const { selectedPayByOption } = this.lastShoppingCartChangedData ?? {};

        return {
            payment: payment ?? !!payment_flow,
            waitlist: waitlist ?? reservation_type === 'waitinglist',
            payment_method: payment_method ?? selectedPayByOption ?? '',
        };
    }

    getMergedUpdatedEventParticipant= (updatedParticipantObject: Partial<IObjectWithId>) => {
        return {
            ...this.eventParticipant,
            ...updatedParticipantObject,
            id: this.eventParticipant?.id
        };
    };

    private async createShoppingCartV2Data(shouldShowDiscountCodeField: boolean): Promise<IShoppingCartV2Data> {
        const shoppingCartV2SectionEl = this.domSvc.getDynamicContainerChildElement(this.domSvc.getAttributeSelector(AttributeName.azaShoppingCartV2Section));
        const scanned_registrations = this.processFormResponse?.multiregistration ?
            (await this.multiRegistrationSvc.initMultiRegistrationData(this.processFormResponse.multiregistration)).multiRegistrationData.scanned_registrations:
            [];
        return {
            event: {
                id: this.pageData.event.id,
                name: this.eventName,
                startDate: this.eventStartDate,
                endDate: this.eventEndDate
            },
            participant: this.eventParticipant,
            eventDependencies: this.eventDependencies ?? [],
            productRespone: this.productResponse,
            orders: this.orders,
            // TODO: When to set the cart to read only ?
            readOnly: false,
            showDiscountCode: shouldShowDiscountCodeField,
            selectedPayByOption: null,
            // trim() is required because the attribute could contains an empty space
            onlinePaymentDependency: shoppingCartV2SectionEl?.getAttribute(AttributeName.azaOnlinePaymentDependency)?.trim(),
            invoicePaymentDependency: shoppingCartV2SectionEl?.getAttribute(AttributeName.azaInvoicePaymentDependency)?.trim(),
            dependency: shoppingCartV2SectionEl?.getAttribute(AttributeName.azaDependency)?.trim(),
            scanned_registrations,
        };
    }
}

const isContainingSameMember = <T extends keyof any>(a: T[], b: T[]): boolean => {
    if (a.length !== b.length) {
        return false;
    }
    const mapOfA: Record<T, true> = {} as any;
    a.forEach(item => mapOfA[item] = true);
    return b.every(item => mapOfA[item]);
};

interface IIsLoginPageResult {
    isLogIn: boolean;
    loginFormContainer?: HTMLElement;
    usernameEl?: HTMLInputElement;
    passwordEl?: HTMLInputElement;
    loginButtonSectionEl?: HTMLElement;
    loginButtonEl?: HTMLButtonElement;
    passwordResetButtonSectionEl?: HTMLElement;
    passwordResetButtonEl?: HTMLButtonElement;
    createAccountButtonSectionEl?: HTMLElement;
    createAccountButtonEl?: HTMLButtonElement;
    accessCodeContainerEl?: HTMLElement;
    accessCodeButtonContainerEl?: HTMLElement;
    loginLinkSectionEl?: HTMLElement;
    loginLinkButtonEl?: HTMLElement;
    accessCodeInputEl?: HTMLInputElement;
    accessCodeButtonEl?: HTMLButtonElement;
}

interface IIsResetPasswordPageResult {
    isResetPassword: boolean;
    passwordEl?: HTMLInputElement;
    repeatPasswordEl?: HTMLInputElement;
    submitButtonEl?: HTMLButtonElement;
}

interface ISetEventPageDataResult {
    hasShoppingCartV2: boolean;
}

interface IPaymentResult {
    success: boolean;
    redirectsExternally?: boolean;
}

interface IReverePaymenetResult {
    success: boolean;
    cancelled: boolean;
}

interface IMonerisPaymentResult {
    success: boolean;
}
